/**
 * Chinese translation dictionary. Phase 1 covers nav + common shell + login,
 * leaving page-internal copy untouched (those stay literal Chinese strings).
 * The `enUS` file mirrors this shape — keep keys in sync.
 */
export default {
  common: {
    save: '保存',
    cancel: '取消',
    confirm: '确认',
    delete: '删除',
    edit: '编辑',
    refresh: '刷新',
    create: '新建',
    search: '搜索',
    loading: '加载中...',
    empty: '暂无数据',
    yes: '是',
    no: '否',
    error: '出错',
    success: '成功',
    warning: '警告',
    back: '返回',
    prompt_title: '提示',
    confirm_title: '确认操作',
  },
  nav: {
    dashboard: '仪表盘',
    platform_admin: '平台管理',
    campaigns: '广告系列',
    ad_management: '广告管理',
    batch_create: '批量创建',
    one_click_launch: '一键刊登',
    one_click_records: '刊登记录',
    creatives: '素材看板',
    creative_lifecycle: '素材生命周期',
    audience_lifecycle: '受众生命周期',
    test_planner: 'AI 测试规划器',
    insights_library: 'AI 经验库',
    experiments: 'A/B 实验',
    ad_library: '广告库',
    autopilot: '自动投放',
    autopilot_policies: '策略',
    autopilot_actions: '动作队列',
    ad_accounts: '广告账户',
    pages: '主页',
    pixels: '像素',
    audiences: '受众',
    reports: '投放绩效',
    pivot_report: '多维透视',
    diagnostics: '广告体检',
    data_quality: '数据质量',
    automation_rules: '自动规则',
    profit: '经营总览',
    profit_drilldown: '下钻分析',
    report_center: '报表中心',
    custom_report: '自定义看板',
    product_report: '商品报表',
    store_report: '商店报表',
    creative_board: '素材看板',
    meta_objective_report: 'Meta 成效目标',
    ai_assistant: 'AI 助手',
    profit_breakdown: '维度拆分',
    commerce: '电商店铺',
    platforms: '渠道授权',
    notifications: '通知中心',
    domain_monitoring: '域名监控',
    tasks: '后台任务',
    settings: '设置',
    logout: '退出登录',
    profile: '个人资料',
  },
  navSection: {
    delivery: '投放管理',
    autopilot: 'Autopilot',
    optimize: '智能优化',
    reports: '报表',
    monitor: '监控告警',
    intel: '市场情报',
    resources: '资源与集成',
  },
  commandPalette: {
    placeholder: '搜索页面或操作...',
    empty: '没有匹配的结果',
    group_actions: '操作',
    action_toggle_locale: '切换语言',
    action_logout: '退出登录',
    action_reload: '刷新当前页',
    help_title: '键盘快捷键',
    help_palette: '命令面板',
    help_help: '快捷键帮助',
    help_close: '关闭',
  },
  layout: {
    open_menu: '打开菜单',
    sync_in_progress: '正在同步 {count} 个任务...',
    sync_recent_failed: '最近 1 小时 {count} 个失败',
    view_progress: '查看进度',
    logout_confirm: '确定要退出登录吗?',
    logout_confirm_btn: '退出',
    mobile_gate_title: '推荐使用桌面浏览器',
    mobile_gate_desc: 'Castvo 后台界面针对桌面端优化(≥1024px 宽度)。在手机上某些功能可能显示异常。',
    mobile_gate_continue: '强制继续',
    tasks_running: '{count} 个后台任务进行中',
    tasks_failed: '最近 1 小时 {count} 个任务失败',
  },
  tasks: {
    title: '后台任务',
    subtitle: '系统派发的异步任务 —— 结构同步、花费报表、Webhook 触发等',
    tab_in_progress: '进行中',
    tab_failed: '失败',
    tab_done: '已完成',
    tab_all: '全部',
    col_status: '状态',
    col_kind: '类型',
    col_target: '渠道/账户',
    col_progress: '进度/结果',
    col_time: '时间',
    col_actions: '操作',
    duration: '耗时',
    retry: '重试',
    empty_title: '暂无任务',
    empty_in_progress_title: '当前没有进行中的任务',
    empty_desc: '点击渠道页发起同步,或等待定时任务自动触发',
    retry_count: '重试 #{count}',
    all_accounts: '(全部账户)',
    pages_target: 'Pages / Boards',
    unknown_error: '未知错误',
    status_queued: '排队中',
    status_running: '运行中',
    status_ok: '完成',
    status_partial: '部分完成',
    status_failed: '失败',
    kind_structure: '结构同步',
    kind_pages: '主页同步',
    kind_spend: '花费报表',
    kind_parent_suffix: ' · 总任务',
    section_automation: '自动规则执行',
    section_commerce: '电商同步',
    section_test_plans: 'AI 测试规划',
    section_none: '无',
    last_synced: '最近同步 {time}',
    msg_load_failed: '加载任务列表失败',
    msg_retried: '已重新派发,正在排队执行',
    msg_retry_failed: '重试失败',
  },
  login: {
    title: '登录',
    subtitle: '多渠道广告管理系统',
    email: '邮箱',
    account: '账号',
    account_placeholder: '用户名 / 邮箱 / 手机号',
    password: '密码',
    submit: '登录',
    support_hint: '遇到问题?联系',
    totp_title: '二步验证',
    totp_subtitle: '请输入你 Authenticator app 上的 6 位验证码',
    totp_code_label: '6 位验证码',
    recovery_code_label: '恢复码',
    submit_totp: '验证',
    use_recovery: '丢失了设备?用恢复码登录',
    use_totp: '← 用 Authenticator 验证码',
    upgrade_notice: '为了安全升级,请重新登录(后续无需此操作)',
    msg_need_credentials: '请输入账号和密码',
    msg_totp_required: '请输入 2FA 验证码',
    msg_login_failed: '登录失败',
    msg_need_recovery: '请输入恢复码',
    msg_need_totp: '请输入 6 位验证码',
    msg_verify_failed: '验证失败',
  },
  settings: {
    tab_profile: '个人资料',
    tab_security: '安全',
    tab_notify: '通知偏好',
    tab_team: '组织账号',
    tab_2fa: '二步验证',
    tab_integrations: '通知集成',
    tab_llm: 'AI 模型',
    tab_token: 'API Token',
    danger_zone: '危险区',
    delete_account: '删除账户',
    leave_workspace: '退出工作区',
    page_title: '设置',
    page_subtitle: '个人资料、安全、通知偏好与系统配置',
    profile: {
      card_title: '个人资料',
      label_email: '邮箱',
      label_name: '名称',
      placeholder_name: '你的名称',
      label_role: '角色',
      save: '保存修改',
      warn_name_required: '请输入名称',
      success_saved: '已保存',
      error_save_failed: '保存失败',
    },
    account_defaults: {
      card_title: '账号默认项',
      label_currency: '默认币种',
      label_timezone: '默认时区',
      hint_currency: '未设置时默认使用 USD。',
      hint_timezone: '未设置时默认使用 America/New_York。',
      save: '保存默认项',
      success_saved: '账号默认项已保存',
      error_save_failed: '保存失败',
    },
    security: {
      card_title: '修改密码',
      label_current_password: '当前密码',
      label_new_password: '新密码',
      label_confirm_password: '确认新密码',
      submit: '更新密码',
      warn_password_mismatch: '两次输入的新密码不一致',
      warn_password_too_short: '新密码至少 8 位',
      success_changed: '密码已更新',
      error_change_failed: '修改失败',
    },
    notify: {
      card_title: '通知偏好',
      label_in_app: '站内通知',
      in_app_desc: '开启(无法关闭,Bell 角标 + 通知中心始终生效)',
      label_email: '邮件通知(仅 error 级告警)',
      email_desc: '授权失效、同步严重错误等 error 级事件,转发到邮箱。warning 级(如域名异常)仅站内提醒。',
      label_notify_email: '接收邮箱',
      placeholder_notify_email: '留空使用账号邮箱:{email}',
      hint_notify_email: '默认使用账号邮箱;如希望告警发到另一个邮箱(如团队群邮箱),在此填写。',
      label_webhook: 'Webhook(企业版)',
      webhook_desc: '将告警 POST 到自定义 endpoint — 即将上线',
      save: '保存偏好',
      warn_email_invalid: '请输入有效的邮箱',
      success_saved: '通知偏好已保存',
      error_save_failed: '保存失败',
    },
    api_token: {
      card_title: 'API Token',
      btn_refresh: '刷新',
      btn_create: '新建 Token',
      description: '为脚本集成或第三方工具签发独立 Token。Token 仅在创建时显示一次,请妥善保管。',
      col_name: '名称',
      col_last_used: '最近使用',
      col_expires: '过期',
      col_created: '创建于',
      col_actions: '操作',
      tag_current_session: '当前会话',
      btn_revoke: '撤销',
      empty: '还没有 Token,点右上角新建按钮签发一个',
      never_expires: '永久',
      never_used: '未使用',
      load_failed: '加载 Token 失败',
      create_dialog_title: '新建 API Token',
      label_name: '名称',
      placeholder_name: '例如:数据回流脚本 / GitHub Actions',
      label_expires: '有效期',
      expires_30: '30 天',
      expires_90: '90 天',
      expires_365: '一年(推荐)',
      expires_never: '永久(不推荐)',
      submit_create: '创建',
      warn_name_required: '请填写 Token 名称',
      error_create_failed: '创建失败',
      created_dialog_title: '保存好你的 Token',
      created_alert_title: '此 Token 只显示一次',
      created_alert_desc: '离开此页后将无法再次查看。请立即复制保存到安全的位置。',
      btn_copy: '复制',
      btn_close_saved: '已保存,关闭',
      copied_success: '已复制到剪贴板',
      copy_failed: '复制失败,请手动复制',
      revoke_current_confirm: '撤销「{name}」会让当前浏览器会话失效,需要重新登录。是否继续?',
      revoke_confirm: '撤销 Token「{name}」?已发布的脚本会立即失效。',
      revoke_confirm_label: '撤销',
      revoke_success: '已撤销',
      revoke_failed: '撤销失败',
    },
    danger: {
      desc_owner: '删除账户会同时删除整个工作区 — 所有团队成员、广告账户连接、系列、素材都会被永久清除,且无法恢复。',
      desc_member: '退出工作区会让你立即丢失对这里所有资源的访问权,管理员需要重新邀请才能回来。',
      confirm_owner: '这会删除你的账户与整个工作区,所有团队成员、广告账户连接、系列、素材都会被永久清除。无法恢复。',
      confirm_member: '退出工作区会立刻撤销你对这里所有资源的访问。无法自助恢复,需要管理员重新邀请。',
      title_owner: '删除账户与工作区',
      title_member: '退出工作区',
      confirm_label: '我已了解,继续',
      prompt_title: '最终确认',
      prompt_message: '请输入当前密码以确认',
      prompt_confirm: '永久删除',
      prompt_validate_too_short: '密码至少 6 位',
      success_deleted: '账户已删除',
      error_delete_failed: '删除失败',
    },
  },
  rbac: {
    title: '团队权限', admin_only: '仅管理员可访问', load_failed: '加载失败', refresh: '刷新',
    new_department: '新建部门',
    intro: '按部门隔离数据、按角色分配权限、按账户指派范围',
    departments: '部门', members: '成员',
    col_name: '名称', col_type: '类型', col_members: '成员数', col_actions: '操作',
    col_email: '邮箱', col_role: '角色', col_department: '部门', col_accounts: '指派账户',
    no_department: '未分配部门', no_departments: '暂无部门', no_members: '暂无成员', no_accounts: '未指派账户',
    disabled: '已禁用', delete: '删除', unassign: '取消治理', unassigned: '未治理',
    assign_accounts: '指派账户({n})', assign_accounts_title: '指派广告账户',
    assign_accounts_hint: '勾选该成员可访问的广告账户',
    confirm_delete_dept: '确定删除部门「{name}」?', confirm_unassign: '确定取消「{name}」的治理?',
    name_required: '请输入名称', dept_name_ph: '部门名称',
    saved: '已保存', save: '保存', save_failed: '保存失败', deleted: '已删除', delete_failed: '删除失败',
    create: '创建', cancel: '取消',
    roles: {
      admin: '管理员', sub_admin: '子管理员', opt_manager: '优化经理', optimizer: '优化师',
      design_manager: '设计经理', designer: '设计师', analytics_manager: '数据分析经理', analyst: '数据分析师',
    },
    types: { optimization: '优化', design: '设计', analytics: '数据分析' },
  },
  publicNav: {
    home: '首页',
    help: '帮助中心',
    about: '关于我们',
    login: '登录',
    console: '进入控制台',
    try_free: '免费试用',
  },
  publicFooter: {
    tagline: '多渠道广告投放管理平台 · 把 Meta、TikTok 等主流广告渠道与店铺真实经营数据连起来,让广告决策有依据。',
    product: '产品',
    company: '公司',
    legal: '法律',
    features: '功能介绍',
    help: '帮助中心',
    about: '关于我们',
    business: '商务合作',
    support: '技术支持',
    privacy: '隐私政策',
    terms: '服务条款',
    data_use: '数据使用说明',
    data_deletion: '账号 / 数据删除',
    sub_processors: '子处理者列表',
    contact_us: '联系我们',
  },
  landing: {
    nav_features: '功能',
    nav_autopilot: 'Autopilot',
    nav_signin: '登录',
    cta_start: '免费开始使用',
    cta_console: '进入控制台',
    cta_learn: '了解功能 →',
    hero_badge: '支持 Meta · TikTok · Google · Pinterest · Microsoft',
    hero_title_l1: 'Castvo',
    hero_title_l2: '多渠道广告管理平台',
    hero_sub: 'Castvo 是一款 SaaS 应用,用于通过 OAuth 连接 Facebook、Google、TikTok、Pinterest、Microsoft 等广告账户,同步广告数据、管理广告系列 / 广告组 / 广告、生成投放报表,并监控渠道授权与广告表现。',
    hero_trust_1: 'OAuth 2.0 连接广告账户',
    hero_trust_2: '同步与管理广告数据',
    hero_trust_3: '报表分析 + 授权监控',

    section_features_eyebrow: '产品能力',
    section_features_title: '从经营数据 → 自动决策的完整闭环',
    section_features_sub: '不只看 ROAS,看真实 POAS(利润 / 广告花费)。每一项功能都围绕 "用钱产生利润" 这一目标。',

    f_autopilot_title: '商品驱动 Autopilot',
    f_autopilot_desc: '基于库存、毛利、销售速度自动识别商品机会,生成测试广告草稿、自动止损亏损广告、智能扩量胜出者。带 4 层防护(审批 / 熔断 / 一致性 / 自愈)。',
    f_poas_title: '真实利润仪表盘 (POAS)',
    f_poas_desc: '把 Meta 广告花费和 Shopify/WooCommerce 订单数据 join 在一起,计算扣 COGS + 退款后的真实 POAS,而不是只看 Meta 的 ROAS。',
    f_lifecycle_title: '素材生命周期分析',
    f_lifecycle_desc: '自动给每条创意打标签:新星 / 可放量 / 平庸 / 疲劳 / 假阳性。换素材的决策不再靠感觉,靠 CTR 衰减 + 频次 + ROAS 三维证据。',
    f_diagnostics_title: '智能诊断 + AI 测试规划',
    f_diagnostics_desc: '系统自动发现浪费花费、受众重叠、高频次广告;基于历史表现用 LLM 生成下周 A/B 测试方案,推荐角度、文案、目标人群。',
    f_adlibrary_title: '竞品广告库监控',
    f_adlibrary_desc: '基于 Meta Ad Library API 搜索对手投放中的广告 — 创意、文案、投放周期一目了然。收藏到"灵感库"持久化保存。',
    f_commerce_title: '5 大电商平台集成',
    f_commerce_desc: 'Shopify / WooCommerce / Shopline / Shoplazza / 自研系统 一键接入,同步订单 + 商品 + 库存 + 退款。Shopify 支持库存归零实时 webhook 即时止损。',
    f_team_title: '团队 + 数据权限',
    f_team_desc: '管理员邀请子用户,4 级角色(Admin / Operator / Viewer / SuperAdmin),按操作类型细粒度控制 — 投手可调投放但改不了密钥。',
    f_audit_title: '完整审计 + 回滚',
    f_audit_desc: '每一次写操作(暂停广告 / 改预算 / Autopilot 决策)都记审计日志,带 before/after 快照,任何动作可一键回滚。',

    section_autopilot_eyebrow: 'Autopilot 详解',
    section_autopilot_title: '广告投放从"看报表做决策"升级为"系统自动决策"',
    section_autopilot_sub: '5 条核心规则,每条都可独立开关 + 参数调优。审批模式下系统识别机会、生成草稿,你点一下就执行。自动模式 4 层防护保证不会失控。',
    ap_rule_1_title: '新品测试',
    ap_rule_1_desc: '上架 N 天内 + 库存达标 + 毛利达标 → 自动生成测试广告(状态 PAUSED,等你启用)',
    ap_rule_2_title: '亏损止损',
    ap_rule_2_desc: '广告累计花费超阈值但 POAS 低于阈值连续 N 天 → 自动暂停',
    ap_rule_3_title: '缺货关停',
    ap_rule_3_desc: '商品库存 ≤ 阈值 → 立刻暂停所有相关广告(Shopify webhook 秒级响应)',
    ap_rule_4_title: '胜出扩量',
    ap_rule_4_desc: 'POAS 持续达标 N 天 → 自动加预算(单日 +15-20%,有上限)',
    ap_rule_5_title: '疲劳停投',
    ap_rule_5_desc: '频次 > 阈值 或 CTR 跌幅 > 阈值 → 自动暂停换素材',

    section_channels_eyebrow: '支持渠道',
    section_channels_title: '主流广告 + 电商平台一网打尽',

    footer_product: '产品',
    footer_resources: '资源',
    footer_company: '公司',
    footer_legal: '法律',
    footer_privacy: '隐私政策',
    footer_terms: '服务条款',
    footer_data_deletion: '数据删除',
    footer_docs: '文档',
    footer_changelog: '更新日志',
    footer_status: '系统状态',
    footer_contact: '联系我们',
    footer_copyright: '© 2026 Castvo. All rights reserved.',
  },
  adManagement: {
    page_title: '广告管理',
    page_subtitle: '跨渠道统一管理 — 系列 / 广告组 / 广告 三级结构,Facebook 风格表格视图',
    page_subtitle_mode: '{mode} 习惯视图 · {account} / {campaign} / {adset} / {ad} 层级管理',
    ui_mode_facebook: 'Facebook',
    mode_account: '广告账户',
    mode_accounts: '广告账户',
    mode_facebook_campaign: '广告系列',
    mode_facebook_campaigns: '广告系列',
    mode_facebook_adset: '广告组',
    mode_facebook_adsets: '广告组',
    mode_facebook_ad: '广告',
    mode_facebook_ads: '广告',
    create_level: '新建{level}',
    view_level: '查看{level}',

    // Header actions
    refresh: '刷新',
    sync_ads: '同步广告',
    sync_ads_first_pick: '请先选择广告账户',
    sync_ads_dispatched: '已开始同步「{account}」的广告结构,完成后会自动刷新当前列表',
    sync_ads_all_dispatched: '已开始同步全部授权广告账户({count} 个)的广告结构和近 180 天效果数据',
    sync_ads_none: '当前没有可同步的授权广告账户',
    sync_ads_failed: '同步广告失败',
    create: '新建',
    create_campaign: '新建广告系列',
    create_adset: '新建广告组(需先选系列)',
    create_ad: '新建广告(需先选广告组)',

    // Filter bar
    search_placeholder: '搜索名称',
    filter_platform: '渠道',
    filter_ad_account: '广告账户',
    filter_status: '状态',
    search: '搜索',
    reset: '重置',

    // Status options
    status_active: '投放中',
    status_paused: '已暂停',
    status_archived: '已归档',
    status_deleted: '已删除',
    status_disabled: '已禁用',
    status_suspended: '受限',
    status_pending: '待处理',
    status_pending_review: '审核中',
    status_disapproved: '未通过',

    // Tabs
    tab_campaigns: '系列 ({count})',
    tab_adsets: '广告组 ({count})',
    tab_ads: '广告 ({count})',

    // Breadcrumb
    clear_scope: '清除范围',

    // Bulk bar
    bulk_selected: '已选 {count} 个',
    bulk_pause: '批量暂停',
    bulk_resume: '批量恢复',
    bulk_archive: '批量归档',
    bulk_edit: '批量编辑',

    // Action labels (also used in confirm copy)
    action_pause: '暂停',
    action_resume: '恢复',
    action_archive: '归档',
    action_edit: '编辑',
    action_edit_budget: '改预算',
    inline_budget_invalid: '预算需大于 0',

    // Bulk dialog
    bulk_confirm_title: '批量操作',
    bulk_confirm_message: '{label} 选中的 {count} 项?此动作会同步到渠道。',
    bulk_success: '{label} 完成 ({ok})',
    bulk_partial: '{label}:成功 {ok}、失败 {fail}',

    // Single-row confirms / messages
    confirm_destructive_campaign: '{label}「{name}」?动作会同步到渠道,无法撤销。',
    confirm_destructive_simple: '{label}「{name}」?',
    mutate_success: '{name} → {label}',
    mutate_failed: '{label}失败',
    load_failed: '加载失败',

    // Hints
    hint_pick_campaign: '请先在「广告系列」选择或点开一个系列',
    hint_pick_adset: '请先在「广告组」选择或点开一个广告组',
    ads_sort_not_supported: '广告 tab 暂未接通后端排序;按同步时间倒序展示',

    // Campaign table columns
    col_campaign_name: '系列名称',
    col_platform: '渠道',
    col_objective: '目标',
    col_status: '状态',
    col_budget: '预算',
    col_spend: '花费',
    col_purchase_value: '购买价值',
    col_purchases: '购买',
    col_cpa: 'CPA',
    col_impressions: '展示',
    col_reach: '覆盖',
    col_frequency: '频次',
    col_clicks: '点击',
    col_ctr: 'CTR',
    col_cpc: 'CPC',
    col_cpm: 'CPM',
    col_conversions: '转化',
    col_cost_per_conversion: '单次转化费用',
    col_add_to_carts: '加购',
    col_cost_per_add_to_cart: '单次加购费用',
    col_checkouts: '发起结账',
    col_landing_page_views: '落地页浏览',
    col_video_views: '视频播放',
    col_roas: 'ROAS',
    metrics_range: '数据区间',
    col_ad_account: '广告账户',
    col_platform_campaign_id: '系列 ID',
    col_platform_adset_id: '广告组 ID',
    col_platform_ad_id: '广告 ID',
    col_buying_type: '购买类型',
    col_start_time: '开始时间',
    col_end_time: '结束时间',
    col_bid_strategy: '竞价策略',
    col_bid_amount: '出价',
    col_billing_event: '计费事件',
    col_currency: '币种',
    col_balance: '余额',
    col_created_at: '创建时间',
    col_synced_at: '同步时间',
    col_actions: '操作',
    col_parent_campaign_dynamic: '所属{level}',
    col_parent_adset_dynamic: '所属{level}',
    budget_daily_suffix: '/日',
    budget_lifetime_suffix: '/总',

    // Empty states
    empty_accounts_title: '无广告账户',
    empty_accounts_desc: '去渠道授权后同步账户',
    empty_campaigns_title: '无系列',
    empty_campaigns_desc: '去渠道授权 → 同步',
    empty_adsets_title: '无广告组',
    empty_adsets_desc: '去广告系列 tab 选一个系列同步或创建',
    empty_ads_title: '无广告',
    empty_ads_desc: '去广告组 tab 选一个广告组同步或创建',

    // Ad Set table columns
    col_adset_name: '广告组名称',
    col_parent_campaign: '所属系列',
    col_optimization_goal: '优化目标',

    // Ads table columns
    col_preview: '预览',
    col_ad: '广告',
    col_parent_adset: '所属广告组',
    col_platform_creative_id: '素材 ID',
    col_creative_name: '素材名称',
    col_link_url: '落地页',
    col_video_id: '视频 ID',
    col_creative_size: '素材规格',
    col_headline: '标题',
    col_primary_text: '正文',
    col_description: '描述',
    col_creative_cta: '类型 / CTA',
  },
  createDialogs: {
    campaign: {
      title: '新建广告系列',
      unsupported_capability: '{platform} 暂不支持从 Castvo 直接创建:{missing}。请在 {platform} 后台创建,再用 Castvo 管理状态与预算。',
      unsupported_campaign: '系列',
      unsupported_adset: '广告组',
      unsupported_ad: '广告',
      load_accounts_failed: '加载账户失败',
      label_ad_account: '广告账户',
      placeholder_ad_account: '选择广告账户',
      label_name: '系列名称',
      placeholder_name: '例如:618 大促 · 流量',
      label_objective: '营销目标',
      objective_traffic: '流量',
      objective_sales: '销售',
      objective_leads: '获客',
      objective_engagement: '互动',
      objective_awareness: '品牌认知',
      objective_app_promotion: '应用推广',
      label_status: '状态',
      status_paused_recommended: '已暂停(推荐)',
      status_active_now: '立即投放',
      label_budget_mode: '预算类型',
      budget_daily: '每日预算',
      budget_lifetime: '总预算',
      label_budget_amount: '预算金额 (USD)',
      label_start_time: '开始时间',
      label_stop_time: '结束时间',
      placeholder_optional: '可选',
      submit: '创建',
      warn_pick_account: '请选择广告账户',
      warn_name: '请填写系列名称',
      warn_budget: '请填写预算金额',
      success_created: '系列已创建',
      error_create_failed: '创建失败',
    },
    adset: {
      title: '新建广告组 — {name}',
      unsupported: '{platform} 暂不支持从 Castvo 创建广告组,请在 {platform} 后台创建。',
      label_name: '广告组名称',
      placeholder_name: '例如:US · 18-35 · 流量',
      label_status: '状态',
      status_paused: '已暂停',
      status_active_now: '立即投放',
      label_bid_strategy: '出价策略',
      bid_lowest_cost: '最低成本(无上限)',
      bid_cap: '出价上限',
      cost_cap: '成本上限',
      lowest_cost_with_min_roas: '目标成本',
      label_budget_mode: '预算类型',
      budget_daily: '每日预算',
      budget_lifetime: '总预算',
      label_budget_amount: '预算金额 (USD)',
      label_bid_amount: '出价金额 (USD)',
      placeholder_optional: '可选',
      label_optimization_goal: '优化目标',
      opt_link_clicks: '链接点击',
      opt_impressions: '展示',
      opt_reach: '覆盖人数',
      opt_offsite_conversions: '转化',
      opt_lead_generation: '表单提交',
      opt_post_engagement: '互动',
      opt_app_installs: '应用安装',
      opt_landing_page_views: '着陆页浏览',
      label_billing_event: '计费事件',
      billing_impressions: '按展示 (IMPRESSIONS)',
      billing_link_clicks: '按链接点击 (LINK_CLICKS)',
      billing_video_views: '按视频观看 (VIDEO_VIEWS)',
      divider_targeting: '受众定向',
      label_saved_audience: '从 Saved Audience 快速填充(可选)',
      placeholder_saved_audience: '选一个定向模板,自动填充下面字段',
      btn_new_audience: '+ 新建受众',
      empty_saved_audience: '该账户下还没有 Saved Audience。点右侧「新建受众」就地创建,或下面直接手填定向字段。',
      label_countries: '国家 / 地区(ISO 代码)',
      placeholder_countries: '选择国家',
      country_us: '美国 (US)',
      country_ca: '加拿大 (CA)',
      country_gb: '英国 (GB)',
      country_de: '德国 (DE)',
      country_fr: '法国 (FR)',
      country_jp: '日本 (JP)',
      country_au: '澳大利亚 (AU)',
      country_sg: '新加坡 (SG)',
      country_hk: '香港 (HK)',
      country_tw: '台湾 (TW)',
      label_publisher_platforms: '投放版位',
      label_age_min: '年龄下限',
      label_age_max: '年龄上限',
      label_genders: '性别',
      gender_male: '男',
      gender_female: '女',
      divider_conversion: '转化追踪',
      label_pixel: 'Pixel',
      placeholder_pixel: '选择转化 Pixel',
      label_pixel_event: '转化事件',
      pixel_event_purchase: '购买 (PURCHASE)',
      pixel_event_add_to_cart: '加购 (ADD_TO_CART)',
      pixel_event_initiate_checkout: '发起结账 (INITIATE_CHECKOUT)',
      pixel_event_content_view: '着陆页浏览 (CONTENT_VIEW)',
      pixel_event_lead: '留资 (LEAD)',
      pixel_event_complete_registration: '注册 (COMPLETE_REGISTRATION)',
      pixel_event_search: '搜索 (SEARCH)',
      label_start_time: '开始时间',
      label_end_time: '结束时间',
      submit: '创建',
      warn_name: '请填写广告组名称',
      warn_budget: '请填写预算金额',
      warn_countries: '至少选择一个国家',
      warn_pixel: '当前优化目标需要选择 Pixel',
      warn_saved_audience_missing_targeting: '该 Saved Audience 缺少 targeting 数据',
      success_applied_audience: '已应用「{name}」的定向到下面字段',
      success_created: '广告组已创建',
      error_create_failed: '创建失败',
    },
    ad: {
      title: '新建广告 — {name}',
      unsupported: '{platform} 暂不支持从 Castvo 创建广告,请在 {platform} 后台创建。',
      label_name: '广告名称',
      placeholder_name: '例如:618 · 主推图 v1',
      label_status: '状态',
      status_paused: '已暂停',
      status_active_now: '立即投放',
      label_creative_type: '素材类型',
      creative_image: '图片',
      creative_video: '视频',
      label_page: '投放主页 (Page)',
      placeholder_page: '选择投放主页',
      fan_count_suffix: '粉丝',
      label_image: '图片素材',
      placeholder_image: 'https://... 或从素材库选',
      btn_library: '素材库',
      hint_image_hash_ok: '✓ 已绑定 image_hash,FB 创建创意时直接使用,不依赖公网拉取。',
      hint_image_url: '填 URL 时 FB 会去公网抓取;推荐到素材库上传后选 image_hash,更稳定。',
      label_video_id: '视频 ID(需已在 FB 上传)',
      placeholder_video_id: '例如 1234567890',
      label_link_url: '落地页链接 (Link)',
      placeholder_link_url: 'https://...',
      label_display_link: '显示链接(可选)',
      placeholder_display_link: '例如 example.com',
      label_headline: '标题 (Headline)',
      label_primary_text: '主文案 (Primary Text)',
      label_description: '描述 (Description)',
      label_cta: '行动号召 (CTA)',
      cta_learn_more: '了解更多 (LEARN_MORE)',
      cta_shop_now: '立即购买 (SHOP_NOW)',
      cta_download: '立即下载 (DOWNLOAD)',
      cta_book_travel: '立即预订 (BOOK_TRAVEL)',
      cta_sign_up: '立即注册 (SIGN_UP)',
      cta_get_offer: '立即获取 (GET_OFFER)',
      cta_apply_now: '申请 (APPLY_NOW)',
      cta_contact_us: '联系我们 (CONTACT_US)',
      cta_subscribe: '订阅 (SUBSCRIBE)',
      cta_no_button: '无 (NO_BUTTON)',
      preview_label: '预览',
      preview_page_fallback: '主页名',
      preview_sponsored: 'Sponsored · ⊕',
      preview_display_link_fallback: 'example.com',
      preview_headline_fallback: '标题',
      preview_cta_learn_more: '了解更多',
      preview_cta_shop_now: '立即购买',
      preview_cta_download: '下载',
      preview_cta_book_travel: '预订',
      preview_cta_sign_up: '注册',
      preview_cta_get_offer: '获取',
      preview_cta_apply_now: '申请',
      preview_cta_contact_us: '联系',
      preview_cta_subscribe: '订阅',
      submit: '创建',
      warn_name: '请填写广告名称',
      warn_page: '请选择投放主页',
      warn_link: '请填写落地页链接',
      warn_image: '请选择素材或填写图片 URL',
      warn_video_id: '请填写 video_id(需先在 FB 上传视频)',
      success_created: '广告已创建',
      error_create_failed: '创建失败',
      error_load_creatives: '加载素材失败',
      picker_title: '选择素材',
      picker_empty: '素材库还没有图片,先去「素材库」上传。',
    },
  },
  about: {
    hero_badge: '关于我们',
    hero_title: '让广告投放,不再痛苦',
    hero_sub: 'Castvo 由一群曾经在投手、产品、增长岗位工作多年的工程师创立,我们深刻理解多渠道广告投放每天面对的繁琐与风险。我们的目标是,把这些原本分散在 4 个平台的工具,合并成一个清爽、安全、专业的控制台。',

    stat_channels: '主流广告渠道全覆盖',
    stat_capabilities: '专业能力开箱即用',
    stat_sync_latency: '数据同步延迟',
    stat_uptime: '服务可用性目标',

    values_eyebrow: '我们的价值观',
    values_title: '构建产品的四个原则',
    value_focus_title: '专注核心问题',
    value_focus_desc: '我们不做"什么都能干"的工具,只把广告投放管理这一件事做到行业最专业。',
    value_security_title: '数据安全是底线',
    value_security_desc: '所有授权令牌加密存储、永不外泄;合规、隐私、安全永远是产品决策的第一标准。',
    value_team_title: '为专业团队设计',
    value_team_desc: '不堆功能、不做花架子。每一个细节都来自真实广告投放场景下的反复打磨。',
    value_iteration_title: '持续快速迭代',
    value_iteration_desc: '每周稳定发布,听取真实用户反馈,平台 API 变化第一时间跟进。',

    contact_eyebrow: '联系我们',
    contact_title: '需要更多帮助?',
    contact_sub: '无论是产品咨询、商务合作、还是定制需求,我们都欢迎你与我们联系。',
    info_support: '技术支持',
    info_sales: '商务合作',
    info_hours: '工作时间',
    info_hours_value: '周一至周五 09:00 - 18:00(北京时间)',
    info_coverage: '服务范围',
    info_coverage_value: '面向全球客户提供 SaaS 与企业级解决方案',

    form_title: '留下你的需求',
    form_sub: '我们会在 1 个工作日内回复你。',
    form_name: '姓名',
    form_name_ph: '你的名字',
    form_email: '邮箱',
    form_company: '公司(可选)',
    form_company_ph: '公司或团队名',
    form_message: '咨询内容',
    form_message_ph: '告诉我们你的需求 / 痛点 / 当前广告投放规模……',
    form_submit: '提交咨询',

    msg_fill_required: '请完整填写姓名、邮箱与咨询内容',
    msg_mail_opened: '已为你打开邮件客户端,发送即可联系到我们',
    mail_subject_prefix: '咨询',
    mail_label_name: '姓名',
    mail_label_email: '邮箱',
    mail_label_company: '公司',
  },
  help: {
    hero_badge: '帮助中心',
    hero_title: '新手指引 & 常见问题',
    hero_sub: '4 步开始使用 Castvo,以及最常被问到的问题答疑。',
    hero_cta_console: '进入控制台',
    hero_cta_start: '免费开始使用',

    steps_eyebrow: '快速开始',
    steps_title: '4 步完成首次接入',
    step1_title: '注册账号 + 登录',
    step1_desc: '使用邮箱注册一个 Castvo 账号(免费),登录后进入控制台。整个过程不需要信用卡。',
    step2_title: '连接广告渠道',
    step2_desc: '进入"渠道授权"页面,点击 Facebook / Google / TikTok / Pinterest 任一卡片的"连接"按钮,通过 OAuth 2.0 标准流程授权我们读取数据。',
    step3_title: '同步广告数据',
    step3_desc: '授权完成后系统自动开始同步广告账户、系列、广告组、广告。1-2 分钟后即可在"广告账户"等页面看到完整数据。',
    step4_title: '查看报表 / 投放管理',
    step4_desc: '到"报表"页查看跨渠道绩效;到"广告系列"页对在投广告做暂停 / 恢复 / 归档,或新建系列 → 广告组 → 广告。',

    faq_eyebrow: '常见问题',
    faq_title: '使用过程中最常遇到的',
    faq_q1: '初次连接后多久能看到数据?',
    faq_a1: '通常 1-2 分钟。如果是大账户(系列 + 广告超过 1000),可能需要 5-10 分钟,过程中可以在"通知中心"看到同步状态。',
    faq_q2: '为什么 Reports 页有些渠道没有数据?',
    faq_a2: '请确认:(1)已成功授权该渠道;(2)点过"刷新数据";(3)对应账户在所选时间段有花费。FB 的绩效报表是异步生成,首次拉取需要 2-3 分钟。',
    faq_q3: '什么情况下会收到通知?',
    faq_a3: '主要 3 类:(1)授权 token 失效 — 红色 error;(2)同步任务失败 — 橙色 warning;(3)落地页域名不在白名单 — 橙色 warning。点头部的铃铛或进入通知中心查看完整列表。',
    faq_q4: '怎么开启域名监控?',
    faq_a4: '进入"域名监控"→ 点"添加白名单"→ 输入你的正规域名(如 example.com 或 *.example.com)→ 点"立即扫描"即可。每天 02:00 系统自动扫描一次,发现异常会写入通知。',
    faq_q5: '在 Castvo 创建的广告会立即投放吗?',
    faq_a5: '默认创建的所有广告都是"已暂停"状态,以防误投。需要你手动在我们界面或源平台后台点"恢复"才会真正投放,这一设计是有意为之。',
    faq_q6: 'token 失效了怎么办?',
    faq_a6: '到"渠道授权"页面,该账号会显示为"未连接"。点"连接"重新走一遍 OAuth 流程即可。本地已经同步的历史数据不会丢失。',
    faq_q7: '同步频率可以调整吗?',
    faq_a7: '默认同步频率:广告结构每 1-2 小时;绩效报表每天 02:00 自动拉取昨日完整数据。专业版/企业版可调整到每 30 分钟或每 15 分钟。也可随时手动点"刷新数据"。',
    faq_q8: '为什么 Pages / Pixels 列表是空的?',
    faq_a8: 'Pages 和 Pixels 只对 Facebook 渠道有效(Pinterest 把这个叫 Boards)。需要在 Facebook 授权时勾选 pages_read_engagement 等权限。授权后系统会一并同步进来。',

    support_title: '没找到答案?',
    support_sub: '无论是技术问题、Bug 反馈、还是使用建议,我们都欢迎你联系。',
    support_cta_contact: '提交咨询',
    support_email_link: '邮件支持 support@castvo.app →',
  },
  notFound: {
    title: '页面没找到',
    desc_l1: '你访问的页面可能已经被移动、删除或者从未存在过。',
    desc_l2: '请检查链接是否正确,或返回首页继续浏览。',
    cta_console: '返回控制台',
    cta_home: '返回首页',
    cta_back: '返回上一页',
    hint_prefix: '如果你认为这是 Bug,请联系',
  },
  onboarding: {
    title: '快速上手',
    subtitle: '完成以下步骤,开始追踪真实利润',
    progress: '已完成 {done}/{total}',
    dismiss: '不再显示',
    done: '已完成',
    step_connect_platform_title: '连接广告平台',
    step_connect_platform_desc: '授权 Facebook 等渠道,导入广告数据',
    step_connect_platform_cta: '去连接',
    step_connect_store_title: '连接电商店铺',
    step_connect_store_desc: '接入 Shopify 等店铺,同步真实订单与收入',
    step_connect_store_cta: '去连接',
    step_set_product_cost_title: '同步商品并设置成本',
    step_set_product_cost_desc: '为商品填写成本价(COGS),才能算出真实利润',
    step_set_product_cost_cta: '去设置',
    step_create_ad_title: '创建第一条广告',
    step_create_ad_desc: '用叉乘批量创建快速搭建广告系列',
    step_create_ad_cta: '去创建',
    step_setup_automation_title: '设置自动化规则或 Autopilot',
    step_setup_automation_desc: '让系统自动盯盘、调价或暂停低效广告',
    step_setup_automation_cta: '去设置',
    step_view_profit_title: '查看真实利润看板',
    step_view_profit_desc: '一站式查看广告花费、收入与净利润',
    step_view_profit_cta: '去查看',
  },
  dashboard: {
    page_title: '{greeting},{name}',
    page_subtitle: '近 7 天核心指标 · 渠道状态总览 · 最新告警',
    btn_refresh: '刷新',
    btn_sync_all: '全量同步',

    greeting_late_night: '凌晨好',
    greeting_morning: '上午好',
    greeting_noon: '中午好',
    greeting_afternoon: '下午好',
    greeting_evening: '晚上好',

    onboard_title: '欢迎来到 Castvo',
    onboard_sub: '完成下面 3 步即可开始投放与统一管理多渠道广告',
    onboard_step1_title: '连接广告渠道',
    onboard_step1_desc: '授权 Facebook / Google / TikTok / Pinterest 任一渠道',
    onboard_step1_cta: '去连接',
    onboard_step2_title: '同步广告账户',
    onboard_step2_desc: '从已连接的渠道拉取广告账户与历史数据',
    onboard_step2_cta: '立即同步',
    onboard_step3_title: '创建第一个广告系列',
    onboard_step3_desc: '或继续管理已同步的系列',
    onboard_step3_cta: '新建系列',

    kpi_spend: '花费 (近7天)',
    kpi_impressions: '展示',
    kpi_clicks: '点击',
    kpi_ctr: 'CTR',
    kpi_conversions: '转化',
    kpi_roas: 'ROAS',

    card_trend_title: '花费趋势(近 7 天)',
    card_platform_title: '近 7 天渠道分布',
    link_full_report: '详细报表 →',
    link_view_report: '查看报表 →',
    link_manage: '管理 →',
    link_all: '全部 →',

    col_channel: '渠道',
    col_spend: '花费',
    col_impressions: '展示',
    col_clicks: '点击',
    col_conversions: '转化',
    col_roas: 'ROAS',

    empty_table_prefix: '还没有数据。先在',
    empty_table_link_platforms: '渠道授权',
    empty_table_middle: '页连接广告平台,然后到',
    empty_table_link_reports: '报表',
    empty_table_suffix: '页点击"刷新数据"。',

    card_status_title: '渠道状态({connected}/{total})',
    status_connected: '已连接 · {count}',
    status_disconnected: '未连接',

    card_notifications_title: '近期通知',
    empty_notifications: '暂无通知',

    card_team_title: '团队动态',
    empty_team: '暂无操作',
    actor_deleted: '(已注销)',

    card_quick_title: '快捷操作',
    quick_new_campaign: '新建广告系列',
    quick_view_reports: '查看跨渠道报表',
    quick_domain_monitor: '域名监控',
    quick_platforms: '渠道授权管理',

    time_just_now: '刚刚',
    time_minutes_ago: '{n} 分钟前',
    time_hours_ago: '{n} 小时前',

    msg_load_failed: '加载失败',
    msg_sync_failed: '同步失败',
    msg_sync_dispatched: '已派发 {count} 个同步任务,稍候刷新可见最新数据',
  },
  commerce: {
    title: '电商店铺',
    subtitle:
      '接入 Shopify / WooCommerce / Shopline / Shoplazza / 自研系统 等,把真实订单 / 退款 / 毛利接到广告 ROAS 上 — 计算真实 POAS',
    refresh: '刷新',
    connect: '连接店铺',
    col_shop: '店铺',
    col_currency: '币种',
    col_cogs_rate: 'COGS 比率',
    col_per_order_cost: '每单固定成本',
    col_last_sync: '最近同步',
    col_actions: '操作',
    needs_reauth_tag: '需重新授权',
    action_sync: '同步订单',
    action_sync_products: '同步商品',
    product_sync_progress: '同步中 · {count} 件',
    msg_products_synced: '已同步 {count} 件商品，可前往一键刊登选品',
    msg_product_sync_failed: '商品同步失败，请重试',
    action_cost_settings: '成本设置',
    action_register_webhook: '注册 Webhook',
    register_webhook_tip: '注册实时事件 — 库存归零等会即时触发',
    action_reauth: '重新授权',
    reauth_tip: '重新走 Shopify OAuth 拿新的可刷新令牌',
    action_disconnect: '断开',
    empty_title: '还没有接入电商店铺',
    empty_desc: '接入后,系统会拉取你的订单 + 退款 + 折扣,把广告渠道的 ROAS 翻译成真实 POAS',
    sync_not_yet: '尚未同步',
    sync_just_now: '刚刚',
    sync_minutes_ago: '{n} 分钟前',
    sync_hours_ago: '{n} 小时前',
    sync_days_ago: '{n} 天前',
    connect_dialog_title: '连接电商店铺',
    field_provider: '电商平台',
    provider_shopify: 'Shopify',
    provider_shopline: 'Shopline',
    provider_shoplazza: 'Shoplazza',
    provider_woocommerce: 'WooCommerce',
    provider_custom: '自研系统 / API',
    field_shop_domain: '店铺域名',
    shopify_hint: '只需要 *.myshopify.com 部分 — 自定义域名不能用。下一步跳 Shopify 授权页。',
    shopline_hint_pre:
      '填 Shopline 后台地址,只需要 *.myshopline.com 部分。下一步跳 Shopline 授权页。需要先在 Shopline Developer 申请应用并配置回调',
    shoplazza_hint_pre:
      '填 Shoplazza 店铺地址,*.myshoplaza.com(注意只有一个 z)。需要先在 Shoplazza Developer 申请应用并配置回调',
    field_store_url: '店铺地址 (HTTPS)',
    woo_url_hint: '不带尾巴的 / 或路径,只要根域名',
    woo_oauth_btn: '一键授权连接',
    woo_oauth_hint:
      '填你的 WooCommerce 站点地址(如 https://shop.example.com),点授权后在你的后台点「批准」即可,无需手动复制密钥。需要 WooCommerce ≥ 8.5 且站点可公开访问。',
    woo_manual_toggle: '高级:手动填写 API 密钥',
    field_consumer_key: 'Consumer Key',
    field_consumer_secret: 'Consumer Secret',
    woo_secret_hint: '在 Woo 后台:Settings → Advanced → REST API → Add key,选 Read 权限即可。',
    cancel: '取消',
    submit_connect: '授权连接',
    settings_dialog_title: '成本设置 · {name}',
    field_shop_display_name: '店铺显示名',
    field_cost_mode: '成本核算方式',
    cost_mode_ratio: '统一成本比例',
    cost_mode_per_product: '逐商品真实成本',
    cost_mode_hint:
      '统一比例:按收入的固定百分比估算货物成本,简单但偏估算;逐商品:用每个商品的真实采购成本核算,更准确(推荐有商品成本数据时使用)。',
    field_default_cogs: '默认 COGS 比率',
    cogs_hint_ratio: '按净收入的百分比估算货物成本。例如服装类常用 35-45%,3C 用 60-70%。',
    cogs_hint_per_product: '兜底比例:仅对「还没填真实采购成本」的商品生效,已填成本的商品用真实成本核算。',
    field_product_cost: '商品成本',
    manage_product_cost: '管理商品成本',
    product_cost_hint: '为每个商品填写真实采购成本(USD),可从 Shopify 一键拉取预填。',
    field_per_order_cost: '每单固定成本($)',
    per_order_cost_hint: '运费成本 + 支付手续费 + 包材等单笔固定费用',
    save_recalc: '保存(并重算过去 90 天)',
    msg_reauth_redirect: '正在跳转到 Shopify 重新授权…',
    msg_reauth_failed: '发起重新授权失败',
    msg_load_failed: '加载失败',
    msg_shopify_domain_required: '请输入店铺域名,例如 snail-shop.myshopify.com',
    msg_shopify_redirect: '正在跳转到 Shopify 授权…',
    msg_shopline_domain_required: '请输入店铺域名,例如 myshop.myshopline.com',
    msg_shopline_redirect: '正在跳转到 Shopline 授权…',
    msg_shoplazza_domain_required: '请输入店铺域名,例如 myshop.myshoplaza.com',
    msg_shoplazza_redirect: '正在跳转到 Shoplazza 授权…',
    msg_woo_store_required: '请输入店铺地址,例如 https://shop.example.com',
    msg_woo_redirect: '正在跳转到 WooCommerce 授权…',
    msg_woo_fields_required: '请填写完整的店铺地址 + Consumer Key + Consumer Secret',
    msg_connected: '已连接 {name}',
    msg_connect_failed: '连接失败',
    msg_settings_saved: '已保存,正在重算过去 90 天的利润...',
    msg_save_failed: '保存失败',
    msg_webhook_ok: 'Webhook 注册成功 — 库存归零会即时触发暂停广告',
    msg_webhook_partial: '部分失败:{failed}',
    msg_webhook_failed: '注册失败',
    msg_sync_failed: '同步失败:{error}',
    msg_sync_done: '同步完成 · 拉取 {orders} 笔订单,刷新 {dates} 天的 KPI',
    msg_sync_failed_short: '同步失败',
    confirm_disconnect: '断开「{name}」?店铺历史订单数据会一起删除,不可恢复。',
    confirm_disconnect_title: '断开店铺',
    confirm_disconnect_label: '断开',
    msg_disconnected: '已断开',
    msg_action_failed: '操作失败',
    msg_connected_query: '已连接 {provider} {shop}',
    msg_shopify_auto_connected: '如未看到授权页,是因为该店铺此前已授权过 — 已自动完成连接',
    msg_shop_already_connected: '店铺 {shop} 已连接',
    msg_shopify_auth_failed: '发起 Shopify 授权失败',
    custom_blurb:
      '适用于自建商城 / 其他电商系统。创建后你会得到一个接入密钥,让你的系统把订单 POST 到我们的接口,即可使用 POAS / 真实利润归因。',
    custom_field_name: '名称',
    custom_field_currency: '币种',
    custom_field_cost_mode: '成本模式',
    custom_field_default_cogs: '默认成本率',
    custom_default_cogs_hint: '0 ~ 1,例如 0.4 表示 40%',
    custom_field_per_order_cost: '单均固定成本',
    custom_key_title: '接入密钥(仅显示一次)',
    custom_key_warning: '请立即复制保存。密钥只显示这一次,关闭后无法再次查看;丢失可「重置密钥」。',
    custom_key_howto: '把订单 POST 到 {endpoint},请求头加 Authorization: Bearer <密钥>。',
    custom_key_copied: '我已复制保存',
    action_rotate_key: '重置密钥',
    rotate_tip: '生成新的接入密钥,旧密钥立即失效',
    confirm_rotate: '重置「{name}」的接入密钥?旧密钥会立即失效,需要更新你系统里的配置。',
    confirm_rotate_title: '重置接入密钥',
    confirm_rotate_label: '重置',
    msg_custom_name_required: '请输入名称',
  },
  platforms: {
    title: '渠道授权',
    subtitle: '管理各广告平台的 OAuth 授权,支持一个渠道挂载多个账号',
    refresh: '刷新',
    connected: '已连接',
    disconnected: '未连接',
    connect: '连接',
    add_account: '添加账号',
    unnamed: '未命名',
    sync: '同步',
    syncing: '同步中',
    history: '历史',
    disconnect: '解除',
    badge_beta: 'Beta',
    badge_beta_tip: '读数据 + 暂停可用,部分编辑功能开发中',
    badge_connect_only: '仅连接',
    badge_connect_only_tip: '当前仅支持授权连接,数据同步开发中',
    msg_load_failed: '加载渠道列表失败',
    msg_auth_failed: '发起授权失败',
    confirm_disconnect: '确定要解除 {name} 的授权吗?本地的 Token 将被吊销。',
    confirm_disconnect_fallback: '该账号',
    confirm_disconnect_title: '解除授权',
    confirm_disconnect_label: '解除',
    msg_disconnected: '已解除授权',
    msg_disconnect_failed: '解除授权失败',
    msg_sync_dispatched: '已派发 {count} 个同步任务,FB 通常 1–2 分钟,期间可在本页看进度',
    msg_sync_dispatch_failed: '派发同步失败',
    msg_connected: '已连接 {code}',
    sync_just_now: '刚刚',
    sync_seconds_ago: '{n} 秒前',
    sync_minutes_ago: '{n} 分钟前',
    sync_hours_ago: '{n} 小时前',
    sync_in_progress: '同步中 · {done}/{total} 账户',
    sync_queued: '已排队 · {time}',
    sync_running: '同步中... · {time} 开始',
    sync_ok: '上次同步成功 · {time}',
    sync_counts: '账户 {accounts} · 主页 {pages} · 系列 {campaigns} · 广告 {ads}',
    sync_sub_failed: '{n} 个账户失败',
    sync_partial: '部分同步成功 · {time}',
    sync_failed: '同步失败 · {time}',
    history_title: '同步历史',
    history_title_named: '同步历史 · {name}',
    filter_all_kinds: '全部类型',
    filter_all_status: '全部状态',
    kind_structure: '结构同步',
    kind_pages: '主页同步',
    kind_insights: 'Insights',
    status_queued: '已排队',
    status_running: '运行中',
    status_ok: '成功',
    status_partial: '部分成功',
    status_failed: '失败',
    history_empty: '没有匹配的记录(保留 30 天)',
    col_time: '时间',
    col_kind: '类型',
    col_account: '账户',
    col_status: '状态',
    col_duration: '耗时',
    col_result: '结果',
    col_actions: '操作',
    main_task: '— (主任务)',
    retry: '重试',
    retried_n: '重试 {n} 次',
    msg_history_load_failed: '加载历史失败',
    msg_retry_dispatched: '已重新派发',
    msg_retry_failed: '重试失败',
    cnt_accounts: '账户',
    cnt_bm: 'BM',
    cnt_campaigns: '系列',
    cnt_ad_sets: '广告组',
    cnt_ads: '广告',
    cnt_pages: '主页',
    cnt_rows: '行',
  },
  adAccounts: {
    title: '广告账户',
    subtitle: '所有渠道下的广告账户,查看余额 / 状态,管理同步设置',
    refresh: '刷新',
    search_placeholder: '搜索账户名称',
    filter_platform: '渠道',
    filter_status: '状态',
    search: '搜索',
    reset: '重置',
    status_active: '活跃',
    status_disabled: '已禁用',
    status_suspended: '已暂停',
    status_pending: '待处理',
    col_account: '账户',
    col_channel: '渠道',
    col_bm: 'BM',
    col_status: '状态',
    col_currency: '币种',
    col_balance: '余额',
    col_spend_cap: '预算上限',
    col_spend_7d: '花费(近 7 天)',
    col_impressions: '展示',
    col_clicks: '点击',
    col_roas: 'ROAS',
    col_last_sync: '最近同步',
    col_actions: '操作',
    sync: '同步',
    empty_title: '还没有广告账户',
    empty_desc: '去渠道授权页授权一个广告平台,账户会自动同步进来',
    empty_filtered_title: '没有匹配的账户',
    empty_filtered_desc: '试着调整筛选条件或清空搜索',
    msg_load_failed: '加载失败',
    msg_sync_dispatched: '已派发同步任务',
    msg_sync_failed: '同步失败',
  },
  audiences: {
    title: '受众',
    subtitle: '自定义受众、Lookalike、已保存受众 — 跨平台集中查看,支持从渠道同步',
    refresh: '刷新',
    create: '新建受众',
    menu_saved: 'Saved Audience',
    menu_saved_desc: '定向模板(投放给谁)',
    menu_website: 'Website 受众',
    menu_website_desc: '基于 Pixel 的网站访客',
    menu_customer_list: 'Customer List',
    menu_customer_list_desc: '上传客户邮箱/手机(本地哈希)',
    menu_lookalike: 'Lookalike',
    menu_lookalike_desc: '从已有受众拓展类似人群',
    search_placeholder: '搜索受众名称',
    filter_platform: '渠道',
    filter_ad_account: '广告账户',
    filter_type: '类型',
    search: '搜索',
    reset: '重置',
    sync_from_account: '从账户同步...',
    sync_audiences: '同步受众',
    type_custom: '自定义受众',
    type_lookalike: '类似受众',
    type_saved: '已保存受众',
    type_special: '特殊受众',
    col_audience: '受众',
    col_type: '类型',
    col_size: '规模',
    col_description: '描述',
    col_created: '创建时间',
    col_last_sync: '最近同步',
    empty_title: '还没有受众',
    empty_desc: '选择一个广告账户,点右上「同步受众」按钮拉取该账户下的自定义/Lookalike 受众',
    empty_action: '新建受众',
    empty_filtered_title: '没有匹配的受众',
    empty_filtered_desc: '尝试调整筛选条件',
    msg_load_failed: '加载失败',
    msg_select_account: '请选择要同步的广告账户',
    msg_synced: '已同步 {count} 个受众',
    msg_sync_failed: '同步失败',
  },
  notifications: {
    title: '通知中心',
    subtitle: '授权失效、同步失败、域名异常等告警,以及系统消息',
    refresh: '刷新',
    mark_all_read: '全部标记已读',
    tab_all: '全部',
    tab_auth_expired: '授权失效',
    tab_sync_failed: '同步异常',
    tab_domain_alert: '域名告警',
    tab_policy_event: '政策事件',
    tab_system: '系统',
    filter_all_levels: '全部级别',
    level_info: '信息',
    level_warning: '警告',
    level_error: '错误',
    unread_only: '仅未读',
    resolve: '去处理',
    delete: '删除',
    empty_filtered_title: '此分类暂无通知',
    empty_title: '暂无通知',
    empty_desc: '授权失效、心跳异常、域名监控告警都会出现在这里',
    msg_load_failed: '加载失败',
    msg_mark_failed: '标记失败',
    msg_all_read: '已全部标记为已读',
    msg_action_failed: '操作失败',
    confirm_delete: '删除这条通知?',
    confirm_delete_label: '删除',
    msg_delete_failed: '删除失败',
  },
  productCost: {
    title: '商品成本 · {name}',
    search_placeholder: '按商品标题搜索',
    search: '搜索',
    refresh: '刷新',
    sync_from_shopify: '从 Shopify 拉取成本',
    col_title: '商品标题',
    col_price: '当前售价',
    col_unit_cost: '采购成本(USD)',
    unit_cost_placeholder: '未设',
    empty_title: '还没有同步商品',
    empty_desc: '该店铺还没有同步商品,先在「电商店铺」页点同步,或点「从 Shopify 拉取成本」',
    done: '完成',
    msg_load_failed: '加载失败',
    msg_saved: '已保存',
    msg_save_failed: '保存失败',
    msg_costs_synced: '已从 Shopify 拉取 {updated} 个商品成本',
    msg_sync_failed: '拉取成本失败',
  },
  creatives: {
    title: '素材看板',
    subtitle: '统一管理素材资产、效果排行与标签维度分析',
    report_title: '素材看板',
    report_subtitle: '素材资产、优质素材和标签维度分析统一在本页查看',
    mode_gallery: '画廊',
    mode_analytics: '效果分析',
    open_reports: '查看素材报表',
    back_to_library: '返回素材库',
    refresh: '刷新',
    upload: '上传素材',
    search_placeholder: '搜索素材名称',
    ad_account_placeholder: '广告账户',
    type_placeholder: '类型',
    type_image: '图片',
    type_video: '视频',
    type_carousel: '轮播',
    type_collection: '合集',
    type_text: '文案',
    search: '搜索',
    reset: '重置',
    window_label: '时间窗口',
    window_3d: '近 3 天',
    window_7d: '近 7 天',
    stage_label: '阶段',
    sort_label: '排序',
    sort_default: '默认',
    sort_roas: 'ROAS',
    sort_spend: '花费',
    sort_ctr: 'CTR',
    sort_conversions: '转化数',
    sort_desc: '降序',
    sort_asc: '升序',
    stage_all: '全部',
    stage_rising_star: '🌟 新星',
    stage_scale_worthy: '🚀 可放量',
    stage_just_launched: '🆕 刚启动',
    stage_mediocre: '🟡 平庸',
    stage_fatigued: '🔥 疲劳',
    stage_false_positive: '⚪ 假阳性',
    gallery_empty_title: '素材库是空的',
    gallery_empty_desc: '点上传按钮添加图片/视频,或从已授权渠道同步现有素材',
    gallery_nomatch_title: '没有匹配的素材',
    gallery_nomatch_desc: '尝试调整筛选条件',
    fb_ready: 'FB ready',
    fb_ready_tip: '已注册到 FB,创建广告时使用 image_hash',
    delete: '删除',
    analytics_empty_title: '暂无素材效果数据',
    analytics_empty_desc: '切换时间窗口或阶段筛选,或检查广告是否已投放',
    col_thumbnail: '缩略图',
    col_name: '名称',
    col_type: '类型',
    col_stage: '阶段',
    col_spend: '花费',
    col_roas: 'ROAS',
    col_ctr: 'CTR',
    col_conversions: '转化',
    col_ad_count: '广告数',
    col_launched: '启动',
    col_actions: '操作',
    no_data: '无数据',
    ad_count_tip: '用于 {count} 条广告',
    detail: '详情',
    drawer_title_default: '素材详情',
    perf_summary: '效果摘要',
    no_perf_data_desc: '该素材尚未关联到投放数据',
    stat_spend: '花费',
    stat_roas: 'ROAS',
    stat_ctr: 'CTR',
    stat_conversions: '转化',
    m_impressions: '展示',
    m_clicks: '点击',
    m_purchases: '购买',
    m_purchase_value: '购买价值',
    m_reach: '触达',
    m_frequency: '频次',
    m_cpa: 'CPA',
    m_launched: '启动',
    ads_using: '使用此素材的广告 ({count})',
    no_related_ads: '暂无关联广告',
    ad_link: '广告 #{id}',
    upload_title: '上传素材',
    upload_ad_account: '广告账户',
    upload_ad_account_placeholder: '选择账户',
    upload_file: '文件',
    upload_hint: '图片 ≤ 10MB(jpg/png/gif/webp);视频 ≤ 200MB(mp4/mov/webm)',
    upload_preview: '预览',
    upload_name: '名称(可选)',
    upload_name_placeholder: '留空使用文件名',
    cancel: '取消',
    upload_btn: '上传',
    relative_minutes: '{count} 分钟前',
    relative_hours: '{count} 小时前',
    relative_days: '{count} 天前',
    relative_months: '{count} 个月前',
    relative_years: '{count} 年前',
    msg_load_failed: '加载失败',
    msg_pick_file: '请选择文件',
    msg_pick_account: '请选择广告账户',
    msg_uploaded: '已上传',
    msg_upload_failed: '上传失败',
    confirm_delete: '删除素材「{name}」?',
    confirm_delete_label: '删除',
    msg_deleted: '已删除',
    msg_delete_failed: '删除失败',
  },
  creativeLifecycle: {
    preview_title: "预览素材",
    preview_version: "切换素材记录",
    preview_version_label: "素材 {index} · ID {id}",
    preview_unavailable: "素材暂时无法预览，可能尚未同步、已失效或没有访问权限。",
    preview_retry: "重新加载",
    preview_poster_only: "当前仅有视频封面，暂无可播放视频。",
    preview_material_only: "这里展示素材本身；广告文案、按钮和版位效果以平台实际投放为准。",
    preview_close: "关闭",

    title: '创意疲劳预警 / 生命周期管理',
    subtitle: '按创意聚合所有挂载的广告,自动判定阶段 — 新星 / 可放量 / 疲劳 / 假阳性,让换素材决策有依据',
    refresh: '刷新',
    stage_all: '全部',
    stage_just_launched: '新建',
    stage_rising_star: '新星',
    stage_scale_worthy: '可放量',
    stage_mediocre: '平庸',
    stage_fatigued: '疲劳',
    stage_false_positive: '假阳性',
    callout_good_label: '本周「可放量」创意花费',
    callout_good_hint: '这些是当前账户的胜出者 — 优先复制 / 加预算',
    callout_bad_label: '本周「疲劳 + 假阳性」创意花费',
    callout_bad_hint: '考虑停掉换新素材,或者标记「下次别复用此角度」',
    col_creative: '创意',
    on_n_ads: '挂在 {count} 个广告',
    tag_btn: '标签',
    col_stage: '阶段',
    age_days: '{count} 天',
    col_reason: '判定依据',
    col_ctr_7d: 'CTR (7天)',
    col_frequency_7d: '频次 (7天)',
    col_cpa_7d: 'CPA (7天)',
    col_roas_7d: 'ROAS (7天)',
    col_spend_7d: '7天花费',
    col_traffic_7d: '7天流量',
    col_conversion_7d: '7天转化',
    col_efficiency_7d: '7天效率',
    col_spend_window: '花费 / 触达',
    metric_impressions: '展示',
    metric_clicks: '点击',
    metric_purchases: '购买',
    metric_conversions: '转化',
    metric_revenue: '收入',
    metric_frequency: '频次',
    metric_spend_7d: '7天',
    metric_spend_3d: '3天',
    metric_reach: '触达',
    launched_at: '上线 {date}',
    material_group_counts: '同素材组: {creatives} 个素材 · {ads} 个广告 · {adsets} 个广告组 · {campaigns} 个系列 · {accounts} 个账户',
    material_source_label: '识别依据',
    material_source_content_sha256: '文件内容',
    material_source_fb_image_hash: 'FB 图片 Hash',
    material_source_video_id: '视频 ID',
    material_source_source_url: '原始素材 URL',
    material_source_file_url: '文件 URL',
    material_source_thumbnail_url_fallback: '缩略图兜底',
    material_source_platform_creative_id: '平台素材 ID',
    material_source_creative_id_fallback: '本地素材 ID 兜底',
    material_source_unknown: '未知',
    col_actions: '操作',
    view_ads: '查看广告',
    empty_title: '此分类暂无创意',
    empty_desc: '新创意上线 3 天后才会有判定结果。期间可以先打标签方便回溯。',
    tag_type_hook: 'Hook',
    tag_type_angle: '角度',
    tag_type_format: '格式',
    tag_type_selling_point: '卖点',
    tag_type_custom: '自定义',
    stage_hint_just_launched: '刚上线,数据不足',
    stage_hint_rising_star: '早期但已超基线,可考虑增预算测水',
    stage_hint_scale_worthy: '稳定表现好,放量主力',
    stage_hint_mediocre: '平均水平,可继续观察或拆测试',
    stage_hint_fatigued: '指标下滑,建议换新素材',
    stage_hint_false_positive: '首日好后续崩,下次别复用此角度',
    dialog_title: '为「{name}」添加标签',
    dialog_tag_type: '标签类型',
    dialog_tag_value: '标签值',
    dialog_tag_value_placeholder: '例如:痛点 / 价格优势 / UGC / 对比图',
    dialog_current: '已有标签',
    dialog_close: '关闭',
    dialog_add: '添加',
    msg_load_failed: '加载失败',
    msg_input_tag: '请输入标签内容',
    msg_added: '已添加',
    msg_add_failed: '添加失败',
    msg_delete_failed: '删除失败',
  },
  adLibrary: {
    title: '广告库 · 竞品监控',
    subtitle: '基于 Meta Ad Library 搜索竞争对手投放中的广告 — 创意、文案、投放周期一目了然。其他渠道开发中。',
    notice_title: 'Meta Ad Library 数据覆盖说明',
    notice_intro: 'Meta 公开广告库的覆盖范围有限,搜不到广告通常是因为数据本身不在覆盖范围内,不是系统故障:',
    notice_li_eu: '欧盟成员国 + 英国(DE / FR / IT / ES / NL / SE / GB 等):依据 DSA 法案,所有类型的广告都对外公开 — 商业、品牌、政治都可查。',
    notice_li_us: '美国 / 全球其他地区:Meta 只公开「政治和议题广告」。如果搜 nike / adidas 这类商业广告,选 US 通常 0 结果 — 把「广告类型」切到「政治/议题」才看得到 US 数据。',
    notice_li_pageid: 'Page ID 比关键词更准:在 facebook.com 找到对手主页的 numeric id 粘到这里,可避开品牌词模糊匹配。',
    notice_li_empty: '查询返回空 ≠ 系统出错;有错误会直接弹消息。',
    tab_search: '搜索',
    tab_favorites: '灵感库 ({count})',
    search_type_fuzzy: '模糊',
    search_type_exact: '精准',
    apify_loading_hint: '自动抓取较慢,正在抓取实时广告,请稍候(约 10–60 秒)…',
    view_on_meta: '在 Meta 查看 ↗',
    country_label: '国家(必填)',
    country_placeholder: '选择投放国家',
    terms_label: '关键词(文案 / 品牌)',
    terms_placeholder: '如:nike running、海运、护肤精华',
    page_id_label: 'Page ID(可选,逗号分隔 — 比关键词更精准)',
    page_id_placeholder: '例:15087023444,42155876830',
    status_label: '状态',
    status_active: '投放中',
    status_inactive: '历史',
    status_all: '全部',
    ad_type_label: '广告类型',
    ad_type_all: '所有(欧盟+英国可见)',
    ad_type_political: '政治/议题(全球可见)',
    media_label: '媒体类型',
    media_all: '全部',
    media_image: '图片',
    media_video: '视频',
    media_meme: 'Meme',
    platform_label: '平台',
    platform_placeholder: '不限',
    date_min_label: '投放起始(最早)',
    date_max_label: '投放起始(最晚)',
    date_placeholder: '可选',
    search_btn: '搜索',
    empty_start_title: '开始搜索',
    empty_start_desc: '选好国家(建议先选 DE/GB)+ 关键词或 Page ID,点搜索。',
    empty_nores_title: '此条件下没有返回任何广告',
    empty_nores_desc: '常见原因:1) 国家不在 DSA 覆盖范围(美国/亚洲等只能搜政治广告);2) 关键词太具体 — 换品牌词或填 Page ID 试试;3) 投放中没有匹配广告 — 状态切到「历史」或「全部」。',
    unknown_advertiser: '未知广告主',
    delivering: '投放中',
    delivered_for: '已投 {duration}',
    favorited: '已收藏',
    favorite_to: '收藏到灵感库',
    no_preview: '无创意预览',
    chip_spend: '花费 {range} {currency}',
    chip_impressions: '展示 {range}',
    fb_original: 'FB 原页 ↗',
    load_more: '加载下一页',
    fav_empty_title: '灵感库还是空的',
    fav_empty_desc: '在「搜索」Tab 找到喜欢的对手广告,点星标收藏到这里',
    favorited_at: '收藏于 {date}',
    remove: '移除',
    days: '{count} 天',
    msg_pick_country: '请选择至少一个国家',
    msg_input_terms: '请输入关键词搜索(也可填竞品 Page ID 查看其全部广告)',
    msg_search_failed: '搜索失败',
    msg_load_fav_failed: '加载收藏失败',
    msg_favorited: '已收藏到灵感库',
    msg_favorite_failed: '收藏失败',
    confirm_remove: '从灵感库移除「{name}」?',
    confirm_remove_label: '移除',
    msg_remove_failed: '移除失败',
    pick_from_products: '从商品选择',
    picker_title: '从我的商品选关键词',
    picker_search_placeholder: '搜索商品标题…',
    picker_empty_title: '还没有同步的商品',
    picker_empty_desc: '先在「电商渠道」连接店铺并同步商品,这里就会列出可用的关键词来源。',
    picker_untitled: '未命名商品',
    picker_use_keyword: '用作关键词',
    picker_ai_expand: 'AI 扩展竞品词',
    picker_expanded_hint: '点选一个关键词填入搜索框:',
    picker_expand_cost: '本次 AI 扩展花费 ≈ ${cost}',
    msg_load_products_failed: '加载商品失败',
    msg_pick_product: '请先勾选至少一个商品',
    msg_expand_failed: 'AI 扩展失败',
  },
  automationRules: {
    form_channel: '下发渠道',
    channel_default: '仅站内 + 邮件(默认)',
    channel_hint: '除站内信和邮件外,额外把命中通知推送到所选 IM 渠道。',
    title: '自动规则',
    subtitle: '多条件触发 + 一键执行 / 通知 / 调预算 — 把广告体检里发现的浪费自动化处理掉',
    refresh: '刷新',
    create: '新建规则',
    col_rule: '规则',
    col_action: '动作',
    col_interval: '频率',
    col_status: '状态',
    col_last_run: '最近运行',
    col_actions: '操作',
    run_now: '立即运行',
    executions: '执行历史',
    edit: '编辑',
    delete: '删除',
    empty_title: '还没有自动规则',
    empty_desc: '把广告体检里看到的浪费模式抽象成规则,系统每天/每小时自动跑一次',
    matched_count: '匹配 {count} 条',
    dialog_create_title: '新建规则',
    dialog_edit_title: '编辑规则',
    form_name: '规则名称',
    form_name_placeholder: '例如:7天花$50无转化自动暂停',
    form_desc: '备注',
    form_desc_placeholder: '可选,描述规则用途',
    form_conditions: '触发条件(全部满足)',
    day_1: '1 天',
    day_3: '3 天',
    day_7: '7 天',
    day_14: '14 天',
    day_30: '30 天',
    add_condition: '添加条件',
    form_action: '执行动作',
    form_budget_pct: '预算调整比例',
    budget_safety_hint: '安全闸:单次最大 ±50%,绝不会把日预算调到 $5 以下',
    form_note: '备注消息(可选)',
    form_note_placeholder: '例如:CPA 高,先观察 3 天再处理',
    form_status: '状态',
    status_simulate_opt: '模拟运行(推荐先试一周)',
    status_enabled_opt: '运行中',
    status_disabled_opt: '已停用',
    form_eval_interval: '评估频率',
    interval_daily_opt: '每天',
    interval_hourly_opt: '每小时(密集监控)',
    backtest_title: '30 天回测',
    backtest_run: '立即测算',
    backtest_empty: '调整条件后会自动模拟过去 30 天的命中情况,告诉你「如果开了会发生什么」',
    backtest_loading: '回测中...',
    backtest_matched: '命中广告',
    backtest_saved: '潜在节省',
    backtest_chart_label: '最近 30 天每日命中数',
    backtest_chart_tip: '{date} · 匹配 {matched} · 评估 {evaluated}',
    backtest_recent: '最近命中(Top 5)',
    cancel: '取消',
    create_btn: '创建',
    save_btn: '保存',
    exec_drawer_title: '执行历史 · {name}',
    exec_col_result: '结果',
    exec_col_ad: '广告',
    exec_col_time: '时间',
    exec_col_evidence: '证据',
    metric_spend: '花费 ($)',
    metric_impressions: '展示',
    metric_clicks: '点击',
    metric_conversions: '转化',
    metric_purchases: '购买',
    metric_purchase_value: '购买金额 ($)',
    metric_ctr: 'CTR (比率)',
    metric_cpc: 'CPC ($)',
    metric_cpm: 'CPM ($)',
    metric_cpa: 'CPA ($)',
    metric_roas: 'ROAS (倍)',
    metric_poas: '真实 POAS(毛利/花费)',
    metric_true_roas: '真实 ROAS(净收入/花费)',
    metric_net_revenue: '真实净收入 ($)',
    metric_gross_profit: '真实毛利 ($)',
    metric_frequency: '频次',
    action_pause: '暂停广告',
    action_notify: '只通知不操作',
    action_raise_budget: '上调预算',
    action_lower_budget: '下调预算',
    status_enabled: '运行中',
    status_disabled: '已停用',
    status_simulate: '模拟运行',
    interval_hourly: '每小时',
    interval_daily: '每天',
    result_matched_simulated: '匹配(模拟)',
    result_success: '已执行',
    result_failed: '失败',
    result_skipped_duplicate: '跳过(冷却中)',
    cond_summary: '{metric} {comp} {threshold} ({days}天)',
    never_run: '从未运行',
    rel_minutes: '{count} 分钟前',
    rel_hours: '{count} 小时前',
    rel_days: '{count} 天前',
    msg_load_failed: '加载失败',
    msg_max_conditions: '最多 8 个条件',
    msg_min_conditions: '至少保留 1 个条件',
    msg_name_required: '请填写规则名称',
    msg_created: '已创建',
    msg_updated: '已更新',
    msg_save_failed: '保存失败',
    msg_backtest_failed: '回测失败',
    msg_status_switched: '已切换为 {status}',
    msg_update_failed: '更新失败',
    msg_run_result: '执行完成 · 评估 {evaluated} 个广告 · 匹配 {matched} · 应用 {applied}',
    msg_run_result_failed: ' · 失败 {failed}',
    msg_run_failed: '运行失败',
    confirm_delete: '删除规则「{name}」?执行历史会一起删除,不可恢复。',
    confirm_delete_title: '删除规则',
    confirm_delete_label: '删除',
    msg_deleted: '已删除',
    msg_delete_failed: '删除失败',
    msg_load_exec_failed: '加载历史失败',
  },
  autopilotActions: {
    title: 'Autopilot 动作队列',
    subtitle: '系统识别的待执行动作 — 审批模式下需手动批准,自动模式由系统直接执行。所有动作都可回滚。',
    refresh: '刷新',
    status_placeholder: '状态',
    status_pending: '待审批',
    status_approved: '已批准',
    status_rejected: '已拒绝',
    status_executing: '执行中',
    status_completed: '已完成',
    status_failed: '失败',
    status_rolled_back: '已回滚',
    policy_placeholder: '策略',
    search: '搜索',
    reset: '重置',
    col_time: '时间',
    col_status: '状态',
    col_action: '动作',
    col_policy: '策略',
    col_target: '对象',
    col_reason: '原因',
    col_actions: '操作',
    target_product: '商品:{name}',
    target_ad: '广告:{name}',
    approve: '批准',
    reject: '拒绝',
    rollback: '回滚',
    detail: '详情',
    need_operator: '需要 operator 及以上权限',
    need_admin: '回滚需要管理员权限',
    empty_title: '队列为空',
    empty_desc: '没有匹配的动作。可以去「策略」配置规则或在仪表盘点「运行策略」立即评估。',
    action_create_campaign: '创建系列',
    action_create_ad_set: '创建广告组',
    action_create_ad: '创建广告',
    action_pause_campaign: '暂停系列',
    action_pause_ad_set: '暂停广告组',
    action_pause_ad: '暂停广告',
    action_resume_campaign: '恢复系列',
    action_resume_ad_set: '恢复广告组',
    action_resume_ad: '恢复广告',
    action_increase_budget: '提高预算',
    action_decrease_budget: '降低预算',
    drawer_title: '动作详情',
    d_action: '动作:',
    d_status: '状态:',
    d_policy: '策略:',
    d_rule: '规则:',
    d_target: '对象:',
    d_target_product: '商品 · {name}',
    d_target_ad: '广告 · {name}',
    d_reason: '原因:',
    d_error: '错误:',
    d_before: '执行前状态:',
    d_after: '执行后状态:',
    timeline: '时间线',
    msg_load_failed: '加载失败',
    msg_approved: '已批准并执行',
    msg_exec_failed: '执行失败',
    msg_action_failed: '操作失败',
    confirm_reject: '拒绝此动作?动作会被标记 rejected,不会再次出现。',
    confirm_reject_label: '拒绝',
    msg_rejected: '已拒绝',
    confirm_rollback: '回滚此动作?会按 before-state 还原 Meta 上的状态/预算。',
    confirm_rollback_label: '回滚',
    msg_rolled_back: '已回滚',
    msg_rollback_failed: '回滚失败',
  },
  targetingPacks: {
    new: '新建定向包',
    new_title: '新建定向包',
    edit_title: '编辑定向包',
    f_name: '名称',
    name_ph: '例如:欧美核心 · 25-45 女',
    f_geo: '投放国家 / 地区',
    geo_ph: '选择或输入国家代码',
    f_age_min: '最小年龄',
    f_age_max: '最大年龄',
    f_genders: '性别',
    gender_male: '男',
    gender_female: '女',
    genders_help: '不勾选 = 不限性别',
    f_interests: '兴趣词(可选)',
    interests_ph: '用英文逗号分隔,例如:Fitness, Yoga',
    msg_saved: '定向包已保存',
    msg_save_failed: '保存失败',
    msg_deleted: '定向包已删除',
    msg_delete_failed: '删除失败',
    confirm_delete: '确定删除定向包「{name}」?',
    err_name_required: '请填写定向包名称',
    err_geo_required: '请至少选择一个国家/地区',
    err_age_range: '年龄上限必须大于等于下限',
  },
  autopilotPolicies: {
    backtest: '回测',
    f_max_frequency: '扩量频次上限',
    f_max_frequency_tip: '当广告组频次(展示/触达)达到该值时,放量规则将拒绝继续加预算,避免给疲劳素材烧钱。留空表示不限制。',
    f_max_frequency_off: '不限制',
    bt_title: '回测:{name}',
    bt_days: '回测天数',
    bt_run: '运行回测',
    bt_hint: '在历史数据上重放该策略,展示「如果开启会执行哪些放量/暂停动作」,不会真正执行任何操作。',
    bt_scale_up: '放量次数',
    bt_pause_loss: '止损暂停',
    bt_pause_fatigue: '疲劳暂停',
    bt_spend_saved: '预计节省花费',
    bt_range: '区间 {from} ~ {to} · 评估实体 {count} 个',
    bt_col_date: '日期',
    bt_col_action: '动作',
    bt_col_entity: '对象',
    bt_col_reason: '原因',
    bt_action_scale: '放量',
    bt_action_pause_loss: '止损',
    bt_action_fatigue: '疲劳',
    bt_ad: '广告',
    bt_adset: '广告组',
    bt_empty_title: '区间内无动作',
    bt_empty_desc: '该策略在所选历史区间内不会触发任何放量或暂停动作。',
    bt_close: '关闭',
    bt_failed: '回测失败',
    title: 'Autopilot 策略',
    subtitle: '一条策略 = 一个机器人 —— 绑定一个广告账户 + 一个店铺连接,系统每日按规则识别商品机会、止损、扩量',
    refresh: '刷新',
    create: '新建策略',
    create_admin_only: '仅管理员可新建策略',
    empty_title: '还没有 Autopilot 策略',
    empty_desc: '新建一条策略,选好广告账户 + 电商连接 + 规则即可。建议先用「审批模式」观察 1-2 周再切换到自动模式。',
    status_active: '运行中',
    auto_paused: '⚠ 自动暂停',
    auto_paused_tip: '系统于 {time} 自动暂停。触发条件:6 小时内累计 3 次执行失败。24 小时冷却后会自动尝试恢复;也可以现在手动改回「运行中」立即恢复。',
    status_paused: '已暂停',
    status_archived: '已归档',
    mode_auto: '自动模式',
    mode_approval: '审批模式',
    meta_account: '广告账户:{account} · 店铺:{shop}',
    meta_poas: '目标 POAS: {poas} · 单品预算上限:{cap}',
    cap_unlimited: '不限',
    toggle_admin_only: '仅管理员可暂停/恢复',
    edit_admin_only: '仅管理员可编辑',
    delete_admin_only: '仅管理员可删除',
    rule_new_product: '新品测试',
    rule_pause_loss: '亏损止损',
    rule_pause_unavailable: '缺货关停',
    rule_scale_winners: '胜出扩量',
    rule_pause_fatigue: '疲劳停投',
    sync_products: '立即同步商品',
    created_at: '创建 {date}',
    last_evaluated: '· 上次评估 {date}',
    dialog_edit_title: '编辑策略',
    dialog_create_title: '新建策略',
    f_name: '策略名称',
    f_mode: '执行模式',
    f_desc: '描述',
    f_ad_account: '广告账户',
    f_commerce: '电商连接',
    f_target_poas: '目标 POAS',
    f_account_cap: '账户日预算上限 (USD)',
    f_per_product_cap: '单品日预算上限 (USD)',
    creator_divider: '自动创建广告默认设置',
    creator_alert_title: '可选 — 不配置时使用内置默认值',
    creator_alert_desc: '新品测试规则触发后系统会自动创建广告。这里指定使用的 FB 主页 / 像素 / 模板,新广告会以「暂停」状态创建,需要审批或手动启用。',
    f_default_page: '默认 FB 主页',
    f_default_page_placeholder: '使用工作区第一个',
    f_default_pixel: '默认转化像素',
    f_default_pixel_placeholder: '使用账户首个像素',
    rules_divider: '规则',
    rule_new_product_desc: '上架天数内 + 库存/毛利达标 → 生成测试广告草稿',
    rnp_age: '上架天数 ≤',
    rnp_budget: '日预算 (USD)',
    rnp_inventory: '最低库存',
    rnp_margin: '最低毛利率 (0-1)',
    rule_pause_loss_desc: '花费超阈值但 POAS 低于阈值连续 N 天 → 暂停广告',
    rpl_spend: '累计花费 ≥',
    rpl_poas: 'POAS 低于',
    rpl_days: '连续天数',
    rule_pause_unavailable_desc: '商品库存低于阈值 → 自动暂停其在投广告',
    rpu_inventory: '库存 ≤',
    rule_scale_winners_desc: 'POAS 持续达标 → 提高预算(有上限保护)',
    rsw_poas: 'POAS ≥',
    rsw_days: '连续天数',
    rsw_ratio: '每次加幅 (0-1)',
    rsw_max_budget: '最高日预算',
    rule_pause_fatigue_desc: '频次过高或 CTR 跌幅过大 → 暂停换素材',
    rpf_frequency: '频次 ≥',
    rpf_ctr_drop: 'CTR 跌幅 ≥ (0-1)',
    cancel: '取消',
    save: '保存',
    msg_load_failed: '加载失败',
    msg_name_required: '请填写名称',
    msg_account_required: '请选择广告账户',
    msg_commerce_required: '请选择电商连接',
    msg_updated: '已更新',
    msg_created: '已创建',
    msg_save_failed: '保存失败',
    msg_resumed: '已恢复',
    msg_paused: '已暂停',
    msg_action_failed: '操作失败',
    confirm_delete: '删除策略「{name}」?已有动作不会被影响。',
    confirm_delete_label: '删除',
    msg_delete_failed: '删除失败',
    msg_synced: '已同步 {count} 个商品',
    msg_sync_failed: '同步失败',
  },

  acceptInvite: {
    loading: '正在验证邀请...',
    invalid_link: '邀请链接无效',
    unavailable: '邀请不可用',
    unavailable_default: '邀请不可用',
    go_login: '去登录',
    join_workspace: '加入工作区',
    invited_by_prefix: '邀请你以',
    invited_by_suffix: '身份加入',
    f_email: '邮箱',
    f_name: '姓名',
    f_name_placeholder: '你的姓名',
    f_password: '设置密码',
    f_password_placeholder: '至少 8 位',
    f_password_confirm: '确认密码',
    submit: '加入并登录',
    role_admin: '管理员',
    role_operator: '操作员',
    role_viewer: '只读',
    msg_name_required: '请填写姓名',
    msg_password_short: '密码至少 8 位',
    msg_password_mismatch: '两次输入的密码不一致',
    msg_welcome: '欢迎加入,{name}!',
    msg_accept_failed: '接受邀请失败',
  },


  pixels: {
    title: '像素',
    subtitle: '转化追踪像素 / 数据源,创建广告时绑定。Meta + TikTok 已自动同步,其他渠道详见下方覆盖表。',
    refresh: '刷新',
    coverage_title: '渠道支持',
    coverage_synced: '已同步',
    coverage_meta: 'Meta(Facebook)',
    coverage_tiktok: 'TikTok Pixel',
    coverage_google: 'Google Ads',
    coverage_google_status: '无对应概念(用 Conversion Action)',
    coverage_pinterest: 'Pinterest Conversion Tag',
    coverage_pinterest_status: '已同步(需 ads:read scope)',
    coverage_microsoft: 'Microsoft Ads',
    coverage_microsoft_status: '规划中(UET Tag)',
    search_placeholder: '搜索像素名称',
    platform_placeholder: '渠道',
    search: '搜索',
    reset: '重置',
    col_name: '名称',
    col_platform: '渠道',
    col_ad_account: '广告账户',
    col_status: '状态',
    col_pixel_id: 'Pixel ID',
    col_synced_at: '最近同步',
    status_active: '活跃',
    copied: '已复制',
    copy_failed: '复制失败',
    empty_filtered: '无匹配像素',
    empty_title: '还没有同步到像素',
    empty_desc: '授权并同步后,广告账户下的 Pixel 会出现在这里',
    load_failed: '加载失败',
  },

  reportCenter: {
    title: '报表中心',
    subtitle: '保存、整理并与团队共享利润报表配置',
    refresh: '刷新',
    foldersTitle: '文件夹',
    railMine: '我的报表',
    railShared: '分享给我的',
    railUnfiled: '未分类',
    folderNew: '新建文件夹',
    folderRename: '重命名',
    folderDelete: '删除',
    folderNamePrompt: '请输入文件夹名称',
    folderNamePlaceholder: '文件夹名称',
    folderNameRequired: '文件夹名称不能为空',
    folderCreated: '文件夹已创建',
    folderCreateFailed: '创建文件夹失败',
    folderRenamed: '已重命名',
    folderRenameFailed: '重命名失败',
    folderDeleteConfirm: '确定删除文件夹「{name}」?其中的报表将变为未分类。',
    folderDeleted: '文件夹已删除',
    folderDeleteFailed: '删除文件夹失败',
    countLabel: '{count} 个报表',
    emptyTitle: '还没有保存的报表',
    emptyDesc: '在利润报表页点击「另存为报表」即可保存配置',
    open: '打开',
    share: '共享',
    unshare: '取消共享',
    shared: '已共享给团队',
    unshared: '已取消共享',
    shareFailed: '操作失败',
    sharedTag: '已共享',
    delete: '删除',
    deleteConfirm: '确定删除报表「{name}」?',
    deleted: '报表已删除',
    deleteFailed: '删除报表失败',
    loadFailed: '加载报表失败',
    type_profit: '利润看板',
    type_drilldown: '下钻报表',
    type_product: '商品报表',
    type_creative: '素材报表',
    type_custom: '自定义',
    saveAs: '另存为报表',
    saveDialogTitle: '另存为报表',
    nameLabel: '报表名称',
    namePlaceholder: '为这份报表配置命名',
    nameRequired: '请输入报表名称',
    folderLabel: '文件夹',
    folderNone: '不放入文件夹',
    shareWithTeam: '共享给团队',
    saved: '报表已保存',
    saveFailed: '保存报表失败',
    builtin: {
      sectionTitle: '系统内置报表',
      groupCommon: '常用看板',
      groupCreative: '素材分析',
      groupCommerce: '商品与店铺',
      groupChannel: '渠道专项',
      groupCustom: '自定义',
      unavailableTag: '待接入',
      unavailable_channel: '{channel} 渠道连接并验证后可用',
      unavailable_feature: '该报表开发中,后续版本提供',
      // 示例看板
      optimizerEfficiency_name: '优化师人效报表',
      optimizerEfficiency_desc: '按优化师 / 设计师维度的人效与产出',
      channelPerformance_name: '投放绩效',
      channelPerformance_desc: '各渠道花费、展示、点击、转化与 ROAS 聚合',
      pivotMatrix_name: '多维透视报表',
      pivotMatrix_desc: '账户 / 广告系列 / 广告组 / 广告逐层下钻透视',
      googleReport_name: 'Google 报表',
      googleReport_desc: 'Google Ads 投放表现总览',
      tiktokShop_name: 'TikTok Shop 报表',
      tiktokShop_desc: 'TikTok Shop 带货销售表现',
      tiktokSpu_name: 'TikTok SPU 报表',
      tiktokSpu_desc: 'TikTok 按 SPU 维度的表现',
      tiktokBidStrategy_name: 'TikTok 出价策略报表',
      tiktokBidStrategy_desc: '按出价策略分组的投放表现',
      googleBidType_name: 'Google 出价类型报表',
      googleBidType_desc: '按出价类型分组的投放表现',
      metaObjective_name: 'Meta 成效目标报表',
      metaObjective_desc: '按成效目标逐层下钻真实利润',
      tiktokPost_name: 'TikTok 帖子看板',
      tiktokPost_desc: 'TikTok 帖子 / 素材维度看板',
      // 系统看板
      comprehensive_name: '经营总览',
      comprehensive_desc: '整体真实利润 / POAS、节奏与渠道占比',
      productReport_name: '商品报表',
      productReport_desc: '近 30 天各商品真实利润与 POAS 排行',
      storeReport_name: '商店报表',
      storeReport_desc: '各店铺销售与真实利润',
      creativeBoard_name: '素材看板',
      creativeBoard_desc: '素材资产、优质素材排行与标签表现统一看板',
      customBoard_name: '自定义看板',
      customBoard_desc: '自选维度 + 指标,透视真实利润',
    },
  },
  profitReport: {
    compare_off: '不对比',
    compare_prev: '上期',
    compare_yoy: '去年同期',
    compare_tooltip: '对比区间:{start} → {end}',
    compare_tooltip_off: '选择对比区间以显示环比/同比 Δ%',
    pace_today: '今日花费',
    pace_yesterday: '昨日花费',
    pace_7d: '近 7 天花费',
    pace_14d: '近 14 天花费',
    pace_poas: 'POAS {value}',
    pace_no_spend: '无花费',
    donut_title: '渠道花费占比',
    donut_name: '花费占比',
    donut_empty: '暂无渠道花费数据',
    donut_unknown: '未知渠道',
    totals_label: '汇总',
    columns_btn: '自定义列',
    columns_title: '显示列',
  },
  storeReport: {
    title: '商店报表',
    subtitle: '各店铺销售、退款、COGS 与真实毛利(按店铺聚合)',
    refresh: '刷新',
    preset_7d: '近 7 天',
    preset_30d: '近 30 天',
    preset_90d: '近 90 天',
    preset_month: '本月',
    start_date: '开始日期',
    end_date: '结束日期',
    totals_label: '合计',
    col_stores: '店铺数',
    col_store: '店铺',
    col_orders: '订单数',
    col_gross_revenue: '总营收',
    col_refund: '退款',
    col_cogs: '成本(COGS)',
    col_net_revenue: '净营收',
    col_gross_profit: '真实毛利',
    mixed_currency_note: '多币种,金额合计已隐藏(各行按本币显示)',
    empty_title: '暂无店铺销售数据',
    empty_desc: '连接电商店铺并完成订单同步后,这里会按店铺汇总销售与真实利润',
    load_failed: '加载商店报表失败',
  },
  breakdown: {
    title: '维度拆分',
    subtitle: '按版位/地区/设备/年龄/性别拆分平台侧指标',
    refresh: '刷新',
    platform_only_note: '平台侧指标按维度拆分;真实利润 POAS 不可按此拆分(订单按广告归因,不含版位/地区/设备/年龄/性别)。',
    dim_placement: '版位',
    dim_region: '地区',
    dim_device: '设备',
    dim_age: '年龄',
    dim_gender: '性别',
    preset_7d: '近7天',
    preset_30d: '近30天',
    preset_90d: '近90天',
    preset_month: '本月',
    start_date: '开始日期',
    end_date: '结束日期',
    chart_title: '各维度花费 (Top 12)',
    totals_label: '合计',
    col_value: '维度值',
    col_spend: '花费',
    col_impressions: '展示',
    col_clicks: '点击',
    col_conversions: '转化',
    col_purchase_value: '平台收入',
    col_platform_roas: '平台 ROAS',
    empty_title: '暂无拆分数据',
    empty_desc: '维度数据来自真实 FB 广告同步;连接广告账户并同步后显示。',
    load_failed: '加载失败',
  },
  aiAssistant: {
    nl_build_btn: 'AI 建规则',
    nl_dialog_title: '用一句话描述你的规则',
    nl_hint: '用自然语言描述,例如「连续3天 POAS 低于0.8 的广告就暂停」,AI 会解析成规则供你确认后保存。',
    nl_placeholder: '例如:把连续3天 POAS 低于0.8 的广告暂停',
    nl_parse_btn: '解析',
    nl_input_required: '请输入至少 2 个字',
    nl_parsed: '已解析,请确认后保存',
    nl_parse_failed: '解析失败',
    page_title: 'AI 助手',
    page_subtitle: '智能诊断归因与自动复盘日报,全部基于你的真实投放与利润数据',
    diag_title: '智能诊断归因',
    diag_pick_campaign: '请选择广告系列',
    diag_pick_range: '请选择时间范围',
    diag_failed: '诊断失败',
    diag_campaign_ph: '选择广告系列',
    diag_start: '开始日期',
    diag_end: '结束日期',
    diag_run: '诊断',
    col_metric: '指标',
    col_current: '当前',
    col_previous: '上一周期',
    col_delta: '变化',
    m_spend: '花费',
    m_gross_profit: '真实毛利',
    m_poas: 'POAS',
    m_true_roas: '真实 ROAS',
    m_ctr: '点击率',
    m_cpa: '单次转化成本',
    m_orders: '订单数',
    causes: '可能原因',
    actions: '建议操作',
    model_cost: '模型:{model} · 预估成本 ${cost}',
    review_title: '自动复盘日报',
    review_refresh: '刷新',
    review_generate: '生成日报',
    review_generated: '日报已生成',
    review_failed: '生成失败',
    review_for: '{date} 复盘',
    review_empty: '暂无复盘日报,点击「生成日报」基于昨日真实数据生成',
    movers_up: '表现提升',
    movers_down: '需关注/浪费花费',
    anomalies: '异常项',
    suggested: '今日建议',
  },
  tags: {
    manage: '管理标签',
    modeTagReport: '标签报表',
    newGroup: '新建一级标签',
    newChild: '新建二级标签',
    nameLabel: '标签名称',
    nameRequired: '请输入标签名称',
    dimensionLabel: '维度(如 format/angle)',
    created: '标签已创建',
    createFailed: '创建标签失败',
    editKeywords: '编辑关键词',
    keywordsHint: '匹配关键词,逗号或换行分隔(留空则按名称匹配)',
    saved: '已保存',
    saveFailed: '保存失败',
    delete: '删除',
    deleteConfirm: '确定删除标签「{name}」?(已打在素材上的标签不会被移除)',
    deleted: '标签已删除',
    deleteFailed: '删除失败',
    import: '批量导入',
    importHint: '每行一条:维度<Tab>父标签<Tab>标签名<Tab>关键词(逗号或Tab分隔)',
    importEmpty: '没有可导入的行',
    imported: '导入完成:新增 {created},跳过 {skipped}',
    importFailed: '导入失败',
    applyAuto: '智能打标',
    autoApplied: '智能打标完成:命中 {matched},新增 {applied}',
    autoApplyFailed: '智能打标失败',
    emptyTitle: '暂无标签',
    emptyDesc: '新建一级标签并添加二级标签来组织你的素材',
    drillDown: '组合下钻',
    reportEmptyTitle: '暂无标签报表数据',
    reportEmptyDesc: '给素材打标并确保有投放数据后再查看',
    colTagType: '维度',
    colTag: '标签',
    colCreatives: '素材数',
  },
  scheduledReport: {
    schedule: '定时推送',
    dialogTitle: '定时推送 · {name}',
    existing: '已有定时任务',
    frequency: '频率',
    freq_daily: '每天',
    freq_weekly: '每周',
    freq_monthly: '每月',
    hourUtc: '推送时间(UTC 整点)',
    weekday: '星期',
    dayOfMonth: '日期',
    wd_0: '周日',
    wd_1: '周一',
    wd_2: '周二',
    wd_3: '周三',
    wd_4: '周四',
    wd_5: '周五',
    wd_6: '周六',
    channels: '推送渠道',
    channelsPlaceholder: '选择飞书/钉钉/企业微信/Webhook 渠道',
    noChannels: '暂无可用渠道,请先在「通知设置」中添加',
    emails: '邮件接收人',
    emailsPlaceholder: '多个邮箱用逗号、空格或分号分隔',
    destSummary: '渠道 {channels} · 邮箱 {emails}',
    needDestination: '请至少选择一个渠道或填写邮箱',
    add: '添加任务',
    save: '保存',
    cancelEdit: '取消编辑',
    edit: '编辑',
    pause: '暂停',
    resume: '启用',
    delete: '删除',
    runNow: '立即推送',
    ranNow: '已推送 · 渠道 {channels} · 邮箱 {emails}',
    runFailed: '立即推送失败',
    created: '定时任务已创建',
    updated: '定时任务已更新',
    deleted: '定时任务已删除',
    saveFailed: '保存失败',
    deleteFailed: '删除失败',
    deleteConfirm: '确定删除该定时推送任务?',
  },
  assetPacks: {
    geo_groups: '地区组',
    creative_groups: '创意组',
    keyword_packages: '关键词包',
    save_geo: '存为地区组',
    save_creative: '存为创意组',
    new_keyword: '新建关键词包',
    save_geo_title: '保存地区组',
    save_creative_title: '保存创意组',
    save_keyword_title: '保存关键词包',
    name_prompt: '为该资产组命名',
    keywords_prompt: '输入关键词,用逗号或换行分隔',
    applied_geo: '已应用地区组「{name}」({n} 个国家)',
    applied_creative: '已应用创意组「{name}」({n} 个素材)',
    msg_saved: '已保存',
    msg_save_failed: '保存失败',
    msg_deleted: '已删除',
    msg_delete_failed: '删除失败',
    confirm_delete: '确定删除「{name}」?',
    err_name_required: '请输入名称',
    err_geo_empty: '请先选择地区',
    err_creative_empty: '请先选择素材',
    err_keywords_required: '请输入至少一个关键词',
  },
  onlineBatchEdit: {
    entry: '在线批量编辑',
    title_campaigns: '在线批量编辑 · 广告系列',
    title_adsets: '在线批量编辑 · 广告组',
    title_ads: '在线批量编辑 · 广告',
    step_edit: '编辑',
    step_preview: '干跑预览',
    step_execute: '执行',
    meta: '已选 {count} 项',
    meta_skipped: '{count} 项渠道不支持,将跳过',
    assign_label: '批量填充',
    assign_status_ph: '状态',
    assign_budget_ph: '日预算',
    assign_bid_ph: '出价',
    assign_factor_ph: '倍数',
    assign_apply: '填充',
    status_on: '开',
    status_off: '关',
    tag_unsupported: '不支持',
    col_name: '名称',
    col_status: '状态',
    col_budget: '日预算',
    col_bid: '出价',
    col_changes: '变更',
    col_op: '操作',
    col_result: '结果',
    col_error: '错误',
    no_writable: '没有可编辑的项(渠道不支持)',
    no_changes: '没有任何改动',
    dry_run_btn: '干跑预览',
    preview_failed: '预览失败',
    summary: '共 {total} · 变更 {changed} · 错误 {errors} · 无变化 {no_change}',
    has_errors: '{count} 项校验未通过,将被排除',
    no_change_cell: '无变化',
    back: '返回',
    execute_btn: '执行 {count} 项改动',
    execute_failed: '提交失败',
    confirm_title: '确认执行',
    confirm_message: '将对 {count} 项实体提交批量修改,确认?',
    confirm_ok: '执行',
    job_name: '在线批量编辑 {level} · {time}',
    level_campaign: '广告系列',
    level_adset: '广告组',
    level_ad: '广告',
    exec_counts: '完成 {done}/{total} · 失败 {failed} · 跳过 {skipped}',
    polling: '实时刷新中',
    retry_btn: '重试失败项',
    retry_failed: '重试失败',
    cancel: '取消',
    close: '关闭',
    item_status_pending: '等待中',
    item_status_updating: '提交中',
    item_status_done: '成功',
    item_status_failed: '失败',
    item_status_skipped: '跳过',
    job_status_pending: '等待中',
    job_status_running: '执行中',
    job_status_partial: '部分成功',
    job_status_done: '完成',
    job_status_failed: '失败',
    job_status_cancelled: '已取消',
  },
  customReport: {
    title: '自定义看板',
    subtitle: '自选维度透视真实利润数据,可保存为报表',
    refresh: '刷新',
    saveAs: '保存为自定义报表',
    dimensionLabel: '维度',
    metricsLabel: '指标',
    metricsPlaceholder: '选择指标',
    filterPlaceholder: '按名称筛选',
    showChart: '显示图表',
    chartTitle: '毛利 Top 12',
    mixed_currency: '含多种货币,仅显示 {currency}',
    load_failed: '加载失败',
    empty_title: '暂无数据',
    empty_desc: '调整维度或日期范围后重试',
    dim_account: '账户',
    dim_campaign: '广告系列',
    dim_adset: '广告组',
    dim_ad: '广告',
    dim_product: '商品',
    dim_creative: '素材',
    metric_spend: '花费',
    metric_net_revenue: '净收入',
    metric_gross_profit: '毛利',
    metric_order_count: '订单数',
    metric_poas: 'POAS',
    metric_true_roas: '真实 ROAS',
    metric_platform_roas: '平台 ROAS',
  },
  profitDashboard: {
    title: '经营总览',
    subtitle: '把广告渠道的 ROAS 和 Shopify 的真实订单 / 退款 / 毛利对齐 — 看哪些渠道在帮你赚钱,哪些在烧钱',
    start_date: '开始日期',
    end_date: '结束日期',
    refresh: '刷新',
    export_csv: '导出 CSV',
    export_empty: '当前区间没有可导出的数据',
    preset_today: '今天',
    preset_yesterday: '昨天',
    preset_7d: '近 7 天',
    preset_30d: '近 30 天',
    preset_90d: '近 90 天',
    preset_month: '本月',
    preset_last_month: '上月',
    compare_switch: '环比上一周期',
    compare_tooltip: '对比 {start} ~ {end}',
    compare_tooltip_auto: '与等长的上一周期对比(↑↓%)',
    series_poas: 'POAS',
    series_true_roas: '真实 ROAS',
    series_platform_roas: '渠道 ROAS',
    axis_money: '金额',
    axis_roas: 'ROAS',
    kpi_spend: '广告花费',
    kpi_orders: '订单数',
    kpi_net_revenue: '净收入',
    kpi_gross_profit: '毛利润',
    kpi_margin: '毛利率 {pct}',
    kpi_platform_roas: '渠道 ROAS',
    kpi_platform_roas_hint: '广告后台口径',
    kpi_poas: 'POAS · 真实利润 ROAS',
    kpi_true_roas: '真实 ROAS {value}',
    gap_title: '真实 ROAS 只有渠道 ROAS 的 {pct}',
    gap_body: '渠道后台说 {platform},但你的银行账户只看到 {true} — 可能是退款 / 折扣 / 跨渠道重复归因导致。检查折扣码与「再营销」系列。',
    chart_title: '每日趋势 — 花费 / 收入 / 利润',
    series_spend: '广告花费',
    series_net_revenue: '净收入',
    series_gross_profit: '毛利润',
    chart_empty_title: '没有日度数据',
    chart_empty_desc: '确认时间区间内有广告花费和电商订单 — 还没接入 Shopify 的话先去「电商店铺」连接一下',
    detail_title: '每日明细',
    col_date: '日期',
    col_spend: '花费',
    col_orders: '订单',
    col_net_revenue: '净收入',
    col_gross_profit: '毛利',
    col_platform_roas: '渠道 ROAS',
    col_true_roas: '真实 ROAS',
    col_poas: 'POAS',
    table_empty_title: '时间区间内没有数据',
    table_empty_desc: '拉宽区间或先去「电商店铺」连接 Shopify',
    load_failed: '加载失败',
    byAd: {
      title: '按广告 · 真实利润',
      caption: '真实 POAS = 毛利 ÷ 花费(已扣 COGS/退款),比平台 ROAS 更接近你真正赚到的钱。',
      info_tooltip:
        'v1 归因为「末次点击 + 已注入 UTM 的广告」(通过批量创建建的广告);老广告 / 无 UTM 的广告不在此表。COGS 口径为比例 / 订单成本(per-product 逐单成本为 v1.1)。',
      mixed_currency: '部分账户/店铺使用不同币种,已按主要币种({currency})展示,其余已排除。',
      col_ad: '广告名',
      col_campaign: '系列',
      col_platform: '平台',
      col_spend: '花费',
      col_orders: '订单',
      col_net_revenue: '真实净收入',
      col_gross_profit: '真实毛利',
      col_poas: '真实 POAS',
      col_true_roas: '真实 ROAS',
      col_platform_roas: '平台 ROAS',
      empty_title: '还没有按广告的真实利润',
      empty_desc:
        '按广告归因需要用「批量创建」建的、带 UTM 跟踪的广告 + 已同步的电商订单。先去批量创建带跟踪的广告,并连接 / 同步店铺订单。',
    },
    byProduct: {
      title: '按商品 · 真实利润',
      caption:
        '商品级真实利润(已扣 COGS/退款)——哪个商品靠广告真正赚钱,XMP 电商版没有。',
      info_tooltip:
        '商品归因依赖「批量创建」注入的商品 UTM 标记 + 已同步的电商订单;无标记的广告 / 老广告不在此表。COGS 口径为比例 / 订单成本(per-product 逐单成本为 v1.1)。',
      mixed_currency: '部分账户/店铺使用不同币种,已按主要币种({currency})展示,其余已排除。',
      col_product: '商品',
      col_ads: '广告数',
      col_spend: '花费',
      col_orders: '订单',
      col_net_revenue: '真实净收入',
      col_gross_profit: '真实毛利',
      col_poas: '真实 POAS',
      col_true_roas: '真实 ROAS',
      empty_title: '还没有按商品的真实利润',
      empty_desc:
        '商品级利润需要用「批量创建」建的、注入商品 UTM 标记的广告 + 已同步的电商订单。先去批量创建带跟踪的广告,并连接 / 同步店铺订单。',
    },
  },

  profitDrilldown: {
    title: '下钻分析',
    subtitle: '账户→系列→广告组→广告 逐层下钻,每层都是真实利润(已扣 COGS/退款)——XMP 只有平台 ROAS。',
    refresh: '刷新',
    start_date: '开始日期',
    end_date: '结束日期',
    preset_today: '今天',
    preset_yesterday: '昨天',
    preset_7d: '近 7 天',
    preset_30d: '近 30 天',
    preset_90d: '近 90 天',
    preset_month: '本月',
    preset_last_month: '上月',
    root_crumb: '全部账户',
    level_account: '账户',
    level_campaign: '系列',
    level_adset: '广告组',
    level_ad: '广告',
    drill_hint: '点击下钻',
    mixed_currency: '部分账户/店铺使用不同币种,已按主要币种({currency})展示,其余已排除。',
    col_name: '名称',
    col_spend: '花费',
    col_orders: '订单',
    col_net_revenue: '真实净收入',
    col_gross_profit: '真实毛利',
    col_poas: '真实 POAS',
    col_true_roas: '真实 ROAS',
    col_platform_roas: '平台 ROAS',
    empty_title: '当前层级没有数据',
    empty_desc: '需要有广告花费 + 已同步的电商订单。先去「电商店铺」连接 / 同步 Shopify,并确认时间区间内有花费。',
    load_failed: '加载失败',
  },

  reports: {
    title: '投放绩效',
    subtitle: '跨渠道投放绩效聚合,支持按日期 / 渠道 / 账户 / 系列分组与导出',
    refresh_data: '刷新数据',
    export_csv: '导出 CSV',
    preset_today: '今天',
    preset_yesterday: '昨天',
    preset_7d: '近 7 天',
    preset_30d: '近 30 天',
    preset_90d: '近 90 天',
    preset_month: '本月',
    preset_last_month: '上月',
    range_start: '开始',
    range_end: '结束',
    platform_all: '全部渠道',
    group_date: '按日期',
    group_platform: '按渠道',
    group_ad_account: '按账户',
    group_campaign: '按系列',
    compare_tooltip: '对比区间:{start} → {end}',
    compare_tooltip_auto: '自动平移到上期',
    compare_switch: '对比上期',
    kpi_spend: '花费',
    kpi_impressions: '展示',
    kpi_clicks: '点击',
    kpi_ctr: 'CTR',
    kpi_conversions: '转化',
    kpi_roas: 'ROAS',
    prev_hint: '上期 {value}',
    legend_spend: 'Spend',
    legend_clicks: 'Clicks',
    legend_conversions: 'Conversions',
    legend_spend_prev: 'Spend(上期)',
    axis_usd: 'USD',
    axis_count: 'Count',
    pie_name: 'Spend by Platform',
    card_trend: '趋势',
    trend_date_only: '趋势图仅在按日期分组时展示。',
    card_pie: '渠道分布(花费)',
    detail_title: '明细 · 按{dimension}',
    dim_date: '日期',
    dim_platform: '渠道',
    dim_ad_account: '账户',
    dim_campaign: '系列',
    dim_default: '维度',
    col_name: '名称',
    col_impressions: '展示',
    col_clicks: '点击',
    col_spend: '花费',
    col_ctr: 'CTR',
    col_cpc: 'CPC',
    col_cpm: 'CPM',
    col_conversions: '转化',
    col_roas: 'ROAS',
    empty_title: '近 7 天还没有报表数据',
    empty_p1_prefix: '连接渠道后,广告结构(账户/系列)会自动同步,但',
    empty_p1_strong: '历史绩效需要单独拉取',
    empty_p1_suffix: '— 跨平台 API 都把 insights 走异步管道。',
    empty_li1: '立即派发 insights 拉取(通常 1-3 分钟,FB 略慢)',
    empty_li1_strong: '「刷新数据」',
    empty_li1_prefix: '点',
    empty_li2: '系统每天 02:00 自动拉一次昨日数据,每小时刷新当天数据',
    empty_li3_prefix: '到',
    empty_li3_link: '渠道授权',
    empty_li3_suffix: '页可看到同步进度',
    empty_refresh: '立即刷新数据',
    msg_sync_dispatched: '已为 {count} 个广告账户派发同步',
    msg_sync_failed: '同步报表失败',
    msg_load_failed: '加载报表失败',
    msg_not_signed_in: '未登录',
    msg_export_failed: '导出失败',
    msg_export_failed_status: '导出失败:{status}',
  },

  campaignDetail: {
    back: '返回',
    title_fallback: '广告系列 #{id}',
    subtitle_fallback: '系列详情',
    edit: '编辑',
    pause: '暂停',
    resume: '恢复',
    archive: '归档',
    meta_status: '状态',
    meta_objective: '目标',
    meta_budget: '预算',
    meta_budget_type: '预算类型',
    budget_daily: '每日',
    budget_lifetime: '总预算',
    meta_start: '开始',
    meta_end: '结束',
    perf_title: '近 7 天绩效',
    perf_empty: '暂无数据 — 该系列还没同步过绩效,或近 7 天没有花费',
    kpi_spend: '花费',
    kpi_impressions: '展示',
    kpi_clicks: '点击',
    kpi_ctr: 'CTR',
    kpi_conversions: '转化',
    kpi_roas: 'ROAS',
    tab_adsets: '广告组',
    tab_ai: 'AI 复盘',
    tab_raw: '原始数据',
    select_all: '全选',
    new_adset: '新建广告组',
    selected_count: '已选 {count} 个广告组',
    bulk_edit: '批量编辑',
    bulk_pause: '批量暂停',
    bulk_resume: '批量恢复',
    bulk_archive: '批量归档',
    clear_selection: '清除选择',
    adset_meta: '预算 {budget} · 出价 {bid}',
    new_ad: '新建广告',
    col_ad_name: '广告名',
    col_status: '状态',
    col_type: '类型',
    col_headline: '标题',
    col_cta: 'CTA',
    col_actions: '操作',
    no_ads: '暂无广告',
    empty_adsets_title: '还没有广告组',
    empty_adsets_desc: '本系列下还没同步到广告组,试着去渠道授权页触发一次同步',
    status_active: '投放中',
    status_paused: '已暂停',
    status_deleted: '已删除',
    status_archived: '已归档',
    status_pending_review: '审核中',
    status_disapproved: '不通过',
    confirm_destructive: '{label}「{name}」?此操作会同步到渠道,无法撤销。',
    msg_done: '已{label}',
    msg_failed: '{label}失败',
    msg_load_failed: '加载失败',
    msg_load_ads_failed: '加载广告失败',
    bulk_none: '没有可{label}的广告组',
    bulk_done: '已{label} {count} 个广告组',
    bulk_partial: '{label}成功 {ok} 个,失败 {failed} 个',
  },

  experiments: {
    title: 'A/B 实验中控台',
    subtitle: '设计假设 + 选择变体 + 跑足天数 + 拿统计显著性 — 避免凭感觉换素材',
    refresh: '刷新',
    new_experiment: '新建实验',
    list_title: '实验 ({count})',
    empty_title: '还没有实验',
    empty_desc: '设计假设 + 选 2-8 个变体,跑统计显著性,沉淀方法论',
    placeholder_title: '选一个实验查看进度',
    placeholder_desc: '左边选实验 / 右上角新建',
    sub_line: 'p < {p} · 最少 {days} 天',
    start: '开始实验',
    conclude: '宣告获胜者',
    abort: '中止',
    delete: '删除',
    hypothesis_label: '假设:{text}',
    progress_line: '已运行 {days} / {min} 天 · ',
    ready_tag: '✓ 可宣告 — 至少一个变体显著',
    need_more_days: '还需 {days} 天',
    not_significant: '仍未达到显著性,继续观察',
    concluded_banner: '✓ 已结束 — 获胜者:{winner}',
    winner_unspecified: '未指定',
    variant_comparison: '变体对比',
    col_variant: '变体',
    tag_control: '对照',
    target_count: '{count} 个目标',
    col_spend: '花费',
    col_impressions: '展示',
    col_clicks: '点击',
    col_conversions: '转化',
    col_verdict: '结论',
    dialog_title: '新建实验',
    field_name: '实验名称',
    field_name_ph: '例如:Hook A/B vs 痛点 vs 价格',
    field_scope: '作用范围',
    scope_ad_id: '广告 ID',
    scope_adset_id: '广告组 ID',
    scope_campaign_id: '系列 ID',
    field_hypothesis: '假设',
    field_hypothesis_ph: '例如:价格对比 hook 的 CTR 比痛点叙事 hook 高 30%',
    field_primary_metric: '主指标(决定胜负)',
    metric_ctr_opt: 'CTR(点击率)',
    metric_conversion_rate_opt: '转化率',
    metric_cpa_opt: 'CPA(越低越好)',
    metric_roas_opt: 'ROAS',
    metric_purchase_count_opt: '购买数',
    field_p_value: 'p 值阈值',
    field_min_days: '最少天数',
    variants_divider: '变体(至少 2 个,最多 8 个)',
    variant_label_ph: 'A / B / C...',
    variant_desc_ph: '变体描述(可选)',
    variant_targets_ph: '{scope} ID,逗号分隔',
    add_variant: '添加变体',
    cancel: '取消',
    create_draft: '创建草稿',
    variant_control_a: 'A (对照)',
    msg_max_variants: '最多 8 个变体',
    msg_min_variants: '至少需要 2 个变体',
    msg_need_name: '请填写实验名称',
    msg_need_hypothesis: '请填写假设',
    msg_need_target: '每个变体都需要至少 1 个 {scope} 的 ID',
    msg_created: '已创建实验(草稿状态,需要点"开始"才会启动统计)',
    msg_create_failed: '创建失败',
    confirm_start_msg: '开始实验「{name}」?统计从此刻开始累积。最少需要 {days} 天才能宣告结果。',
    confirm_start_title: '开始实验',
    confirm_start_ok: '开始',
    msg_started: '已开始',
    msg_action_failed: '操作失败',
    confirm_conclude_abort: '中止实验「{name}」?不会记录获胜者。',
    confirm_conclude_win: '宣告变体 {label} 为获胜者?实验进入"已结束"状态,不再累积统计。',
    conclude_abort_title: '中止实验',
    conclude_win_title: '宣告获胜者',
    msg_aborted: '已中止',
    msg_concluded: '已宣告获胜',
    confirm_delete: '删除实验「{name}」?变体也会一并删除。',
    delete_title: '删除',
    delete_confirm_label: '删除',
    msg_deleted: '已删除',
    msg_delete_failed: '删除失败',
    msg_load_failed: '加载失败',
    msg_load_detail_failed: '加载详情失败',
    status_draft: '草稿',
    status_running: '进行中',
    status_concluded: '已结束',
    status_aborted: '已中止',
    metric_ctr: 'CTR (点击率)',
    metric_conversion_rate: '转化率',
    metric_cpa: 'CPA (每转化成本,越低越好)',
    metric_roas: 'ROAS',
    metric_purchase_count: '购买数',
    scope_ad: '广告',
    scope_adset: '广告组',
    scope_campaign: '系列',
    verdict_control: '对照',
    verdict_winning: '✓ 获胜',
    verdict_losing: '✗ 落败',
    verdict_inconclusive: '差异不显著',
    pvalue_insufficient: '— 样本不足',
    lift_none: '—',
  },

  diagnostics: {
    title: '广告体检',
    subtitle: '自动诊断浪费预算的广告 — 高花费零转化、频次过高、CTR 下滑、CPA 异常等',
    refresh: '刷新',
    run_now: '立即重新分析',
    stat_critical: '严重问题',
    stat_active: '待处理问题',
    stat_wasted: '本周潜在浪费',
    stat_saved: '本月已节省',
    tab_active: '待处理',
    tab_resolved: '已自动解决',
    tab_dismissed: '已忽略',
    tab_all: '全部',
    severity_filter_ph: '全部严重度',
    severity_critical: '严重',
    severity_warning: '警告',
    severity_info: '提示',
    col_severity: '严重度',
    col_problem: '问题',
    col_ad: '广告',
    col_wasted: '浪费预算',
    col_action: '建议动作',
    col_first_seen: '发现时间',
    col_actions: '操作',
    btn_pause: '暂停',
    btn_view: '查看广告',
    btn_dismiss: '忽略',
    empty_active_title: '当前没有待处理问题',
    empty_other_title: '此分类暂无记录',
    empty_active_desc: '所有在投广告的指标都在健康区间。系统每天 03:00 自动重新扫描,你也可以点右上角立即重新分析。',
    empty_other_desc: '等待新一轮扫描后会自动填充',
    msg_run_done: '分析完成 · 扫描 {aggregates} 条广告 · 新增 {created} · 更新 {updated} · 已解决 {resolved}',
    msg_load_summary_failed: '加载概览失败',
    msg_load_failed: '加载诊断失败',
    msg_run_failed: '运行失败',
    confirm_dismiss: '把「{rule}」这条提示从「{ad}」忽略?忽略后不再提醒。',
    dismiss_title: '忽略提示',
    dismiss_ok: '忽略',
    msg_dismissed: '已忽略',
    msg_action_failed: '操作失败',
    confirm_pause: '暂停广告「{ad}」?会立即同步到 {platform},投放停止。',
    pause_title: '一键暂停',
    pause_confirm_label: '暂停',
    msg_paused: '已暂停',
    msg_pause_failed: '暂停失败',
    rule_high_spend_no_conv: '高花费零转化',
    rule_learning_stuck: '学习期卡住',
    rule_frequency_high: '频次过高',
    rule_ctr_decay: 'CTR 下滑',
    rule_cpa_anomaly: 'CPA 异常',
    rule_roas_below_target: 'ROAS 跌穿',
    action_pause: '暂停',
    action_lower_budget: '降预算',
    action_replace_creative: '换素材',
    action_split_audience: '拆受众/测试',
    action_observe: '继续观察',
    metric_high_spend_no_conv: '7 天花费 {spend} 但零转化',
    metric_learning_stuck: '{value} 次转化 / 学习目标 {target}',
    metric_frequency_high: '频次 {value} (阈值 {threshold})',
    metric_ctr_decay: '近 3 天 CTR {value} · 基线 {baseline}',
    metric_cpa_anomaly: '近 3 天 CPA {value} · 基线 {baseline}',
    metric_roas_below_target: '7 天 ROAS {value} (目标 {target})',
    rel_minutes_ago: '{count} 分钟前',
    rel_hours_ago: '{count} 小时前',
    rel_days_ago: '{count} 天前',
  },

  insightsLibrary: {
    title: 'AI 经验库',
    subtitle: '由 Claude 每周自动扫描你的所有广告变更 + 表现数据,提炼可复用的成功决策模式',
    scan_now: '立即扫描',
    refresh: '刷新',
    filter_status_ph: '状态:全部',
    filter_type_ph: '类型:全部',
    opt_all: '全部',
    status_new: '新',
    status_reviewed: '已查看',
    status_applied: '已应用',
    status_dismissed: '已归档',
    type_success: '成功模式',
    type_failure: '失败模式',
    type_recommendation: '建议',
    empty_library: '经验库还是空的 — 点击「立即扫描」让 Claude 提炼你的成功决策',
    empty_filtered: '当前筛选条件下没有经验',
    confidence_high: '高置信',
    confidence_medium: '中等',
    confidence_low: '低置信',
    sample_based: '📊 基于 {count} 条数据',
    evidence: '证据',
    evidence_samples: '样本',
    evidence_data_points: '数据点',
    evidence_reasons: '归因',
    mark_reviewed: '标记已查看',
    archive: '归档',
    apply_as_rule: '应用为规则',
    applied: '已应用',
    view_rule: '查看规则 #{id}',
    restore: '恢复',
    delete: '删除',
    meta_auto: '由 自动周报 生成 · {cost}',
    meta_manual: '由 手动扫描 生成 · {cost}',
    confirm_scan: '本次扫描会消耗一定的 LLM token,通常 30-60 秒完成,确定继续?',
    scan_title: '立即扫描',
    scan_ok: '开始扫描',
    scanning_text: 'Claude 正在分析最近 30 天的广告决策,通常 30-60 秒...',
    msg_generated: '已生成 {count} 条新经验',
    msg_scan_failed: '扫描失败,请稍后再试',
    msg_load_failed: '加载经验库失败',
    msg_action_failed: '操作失败',
    msg_restore_failed: '恢复失败',
    confirm_delete: '确定要删除这条经验?删除后无法恢复。',
    delete_title: '删除经验',
    delete_confirm_label: '删除',
    msg_delete_failed: '删除失败',
    rel_just_now: '刚刚',
    rel_minutes_ago: '{count} 分钟前',
    rel_hours_ago: '{count} 小时前',
    rel_days_ago: '{count} 天前',
    rel_months_ago: '{count} 个月前',
    rel_years_ago: '{count} 年前',
  },

  dataQuality: {
    title: '数据质量监控',
    subtitle: '检查跟踪事件是否丢失 / 突降 / 重复 / 缺少 CAPI — 投放质量的先行指标(目前仅支持 Meta Pixel,其他渠道开发中)',
    refresh: '刷新',
    run_now: '立即重新分析',
    coverage_title: '跨渠道覆盖范围',
    coverage_meta: 'Meta (Facebook Pixel)',
    coverage_meta_status: '✓ 已覆盖 — 事件丢失 / 量级下滑 / Purchase 缺失 / CAPI 启发式',
    coverage_google: 'Google Ads (Conversion Actions)',
    coverage_google_status: '规划中 — Google Ads API conversion_action 度量',
    coverage_tiktok: 'TikTok Pixel',
    coverage_tiktok_status: '规划中 — TikTok Marketing API 事件 stats',
    coverage_pinterest: 'Pinterest Conversion Tag',
    coverage_microsoft: 'Microsoft UET Tag',
    coverage_planned: '规划中',
    stat_critical: '严重问题',
    stat_active: '待处理问题',
    stat_pixels: '监测中的 Pixel',
    tab_active: '待处理',
    tab_resolved: '已自动解决',
    tab_dismissed: '已忽略',
    tab_all: '全部',
    col_severity: '严重度',
    col_problem: '问题',
    col_pixel: 'Pixel',
    col_action: '建议动作',
    col_first_seen: '发现时间',
    col_actions: '操作',
    btn_dismiss: '忽略',
    severity_critical: '严重',
    severity_warning: '警告',
    severity_info: '提示',
    empty_active_title: 'Pixel 数据健康',
    empty_other_title: '此分类暂无记录',
    empty_active_desc: '近 24 小时所有 Pixel 上报正常。每天 03:00 UTC 自动分析。',
    empty_other_desc: '点右上角【立即重新分析】也可以手动跑一次',
    trend_title: '近 14 天事件量趋势',
    trend_empty: '没有 Pixel 数据 · 点右上角「立即重新分析」抓取最新快照',
    yesterday: '昨日',
    msg_run_done: '分析完成 · {pixels} 个 Pixel · 写入 {snapshots} 个快照 · 新增 {created} · 解决 {resolved}',
    msg_run_done_errors: ' · 失败 {errors}',
    msg_load_failed: '加载失败',
    msg_run_failed: '运行失败',
    confirm_dismiss: '忽略 「{pixel}」的「{rule}」提示?忽略后不再提醒。',
    dismiss_title: '忽略提示',
    dismiss_ok: '忽略',
    msg_dismissed: '已忽略',
    msg_action_failed: '操作失败',
    rule_events_stopped: '事件停止上报',
    rule_volume_drop: '事件量骤降',
    rule_missing_capi: '疑似缺失 CAPI',
    rule_missing_purchase_event: '账户在花钱但无 Purchase 事件',
    hint_events_stopped: 'Pixel 在过去 24 小时没有收到任何事件,但账户仍在花钱。可能 Pixel 安装失效 / GTM 被清除 / 域名被屏蔽。',
    hint_volume_drop: '昨天的事件量低于近 7 天均值的 70%。检查落地页脚本是否还在 / 是否有 A/B 测试改了埋点。',
    hint_missing_capi: 'Purchase 事件数 / ViewContent 数 < 0.5%,通常意味着只有浏览器侧上报,服务端 (CAPI) 没接好。开 CAPI 能让 Meta 在 iOS 限制 / 浏览器拦截下仍能拿到转化数据,通常 CPA 直接下降 10–17%。',
    hint_missing_purchase_event: '账户 7 天花费超过 $100,但 Pixel 0 个 Purchase 事件。多半是 Pixel 没接到结算页,或者事件名拼错。',
    action_enable_capi: '配置 CAPI / Conversion API',
    action_check_pixel: '检查 Pixel 安装',
    action_check_event_id: '检查 event_id 去重',
    action_check_purchase_event: '检查 Purchase 事件埋点',
    action_observe: '继续观察',
    metric_events_stopped: '24h 内 0 事件 · 账户消耗 ${spend}',
    metric_volume_drop: '{event} · 昨日 {value} · 7天均值 {baseline} · 跌 {drop}%',
    metric_missing_capi: 'Purchase/ViewContent = {ratio}% (基线 ≥ 0.5%)',
    metric_missing_purchase_event: '7天花费 ${spend} · Purchase 事件 0 个',
    rel_minutes_ago: '{count} 分钟前',
    rel_hours_ago: '{count} 小时前',
    rel_days_ago: '{count} 天前',
  },

  domainMonitoring: {
    title: '域名监控',
    subtitle: '监测正在投放的广告落地页域名,识别被盗投与不在白名单的链接',
    refresh: '刷新',
    add_whitelist: '添加白名单',
    scan_now: '立即扫描',
    stat_whitelist: '白名单域名',
    stat_active: '在投域名',
    stat_flagged: '异常域名(不在白名单)',
    tab_flagged: '异常域名',
    tab_whitelist: '白名单域名',
    col_domain: '域名',
    col_ad: '广告',
    col_landing: '落地页',
    col_status: '状态',
    col_triage: '处置',
    col_label: '备注',
    col_added: '添加时间',
    col_actions: '操作',
    btn_whitelist: '加入白名单',
    btn_pause: '暂停广告',
    btn_remove: '移除',
    badge_wildcard: '通配符',
    flagged_ok_title: '未发现异常',
    flagged_ok_desc: '所有在投广告的落地页域名都在你的白名单内 · {count} 个域名',
    whitelist_empty_title: '还未添加白名单域名',
    whitelist_empty_desc: '添加你自有的域名后,系统会自动比对在投广告的落地页域名,发现不匹配立即告警',
    dialog_title: '添加白名单域名',
    field_domain: '域名',
    field_domain_ph: 'example.com 或 *.example.com',
    domain_hint_exact: '精确匹配',
    domain_hint_wildcard: '匹配所有子域(含 apex)',
    field_label: '备注(可选)',
    field_label_ph: '例如:主站',
    cancel: '取消',
    add: '添加',
    msg_load_failed: '加载失败',
    msg_scan_ok: '扫描完成,无异常域名',
    msg_scan_flagged: '扫描完成,发现 {count} 个异常',
    msg_scan_failed: '扫描失败',
    msg_need_domain: '请输入域名',
    msg_added: '已添加',
    msg_add_failed: '添加失败',
    confirm_remove: '从白名单移除「{domain}」?',
    remove_confirm_label: '移除',
    msg_removed: '已移除',
    msg_delete_failed: '删除失败',
    confirm_whitelist: '把「{domain}」加入白名单?加入后该域名下的所有广告告警都会被清除。',
    whitelist_ok: '加入',
    msg_whitelisted: '已加入白名单,重新扫描中',
    msg_already_whitelisted: '该域名已在白名单中',
    msg_action_failed: '操作失败',
    confirm_pause: '暂停广告「{ad}」?该操作会立即同步到 {platform},投放停止。',
    pause_title: '暂停广告',
    pause_confirm_label: '暂停',
    msg_paused: '已暂停',
    msg_pause_failed: '暂停失败',
    status_active: '投放中',
    status_paused: '已暂停',
  },

  testPlanner: {
    title: 'AI 素材测试规划器',
    subtitle: '基于素材生命周期 + 标签 + 体检结果,自动生成下一周该测什么',
    provider_ph: 'AI 模型',
    provider_default: '平台默认 (Claude)',
    provider_add: '→ 添加自己的 AI 模型',
    refresh: '刷新',
    generate: '生成新计划',
    history_title: '历史计划 ({count})',
    empty_title: '还没有测试计划',
    empty_desc: '点右上角「生成新计划」让 AI 看一遍你当前的素材数据,给出下周该测什么',
    cached_tag: '缓存命中',
    placeholder_title: '选一个计划查看',
    placeholder_desc: '点左边的历史计划查看详细推荐;或直接生成第一份',
    observed_patterns: '🔍 观察到的模式',
    rec_hypothesis: '假设',
    rec_variants: '建议测试的方向({count} 个)',
    rec_audience: '受众/定向',
    rec_audience_default: '沿用现有定向',
    rec_success_metric: '成功指标',
    rec_refs: '数据依据',
    caveats: '⚠️ 注意事项',
    priority_high: '高优先级',
    priority_medium: '中等',
    priority_low: '观察',
    msg_generated: '生成完成 · {count} 条建议 · {model} · {seconds}s · 估算 ${cost}',
    msg_generate_failed: '生成失败',
    need_config_title: '需要配置 AI 模型',
    need_config_ok: '去设置',
    msg_load_failed: '加载失败',
    msg_load_detail_failed: '加载详情失败',
    confirm_delete: '删除计划「{title}」?',
    delete_title: '删除',
    delete_confirm_label: '删除',
    msg_deleted: '已删除',
    msg_delete_failed: '删除失败',
    rel_just_now: '刚刚',
    rel_minutes_ago: '{count} 分钟前',
    rel_hours_ago: '{count} 小时前',
    rel_days_ago: '{count} 天前',
  },

  autopilotDashboard: {
    title: '商品驱动自动投放',
    subtitle: '根据店铺真实经营数据(热卖、新品、库存、毛利)自动给广告打分、识别机会、止损扩量。',
    refresh: '刷新',
    rescore: '重新评分',
    run_policies: '运行策略',
    kpi_active_policies: '活跃策略',
    kpi_total_policies: '总共 {count} 条',
    kpi_pending: '待审批动作',
    kpi_view_queue: '查看队列 →',
    kpi_products: '商品数',
    kpi_sorted_by_score: '按机会分降序',
    pending_panel_title: '待审批动作',
    view_all: '全部 →',
    col_type: '类型',
    col_policy: '策略',
    col_target: '对象',
    col_reason: '原因',
    target_product: '商品:{title}',
    target_ad: '广告:{name}',
    ranking_title: '商品机会排行(前 20)',
    empty_title: '还没有商品数据',
    empty_desc: '去「策略」配置一条 Autopilot 策略,绑定电商连接,系统会在每日 cron 中同步并评分。也可以在策略页手动触发同步。',
    inventory: '库存:{qty}',
    inventory_untracked: '未追踪',
    opportunity_score: '机会分',
    bk_hotness: '热 {value}',
    bk_novelty: '新 {value}',
    bk_margin: '毛 {value}',
    bk_inventory: '库 {value}',
    bk_ad_potential: '告 {value}',
    published_at: '上架:{date}',
    synced_at: '同步:{date}',
    msg_load_failed: '加载失败',
    msg_rescored: '已重新评分',
    msg_rescore_failed: '评分失败',
    msg_evaluated: '已运行 {count} 条策略,新建 {emitted} 条动作',
    msg_evaluate_failed: '评估失败',
  },

  pagesMgmt: {
    title: '主页',
    subtitle: 'Facebook Pages / Pinterest Boards 等,创建广告时用于关联宣传主页',
    sync: '同步主页',
    refresh: '刷新',
    search_ph: '搜索主页名称',
    platform_ph: '渠道',
    search: '搜索',
    reset: '重置',
    col_page: '主页',
    uncategorized: '未分类',
    col_fans: '粉丝数',
    col_bm: 'BM',
    col_id: 'ID',
    col_synced: '最近同步',
    empty_title_filtered: '无匹配的主页',
    empty_title: '还没有同步到主页',
    empty_desc: '授权 Facebook 并同步后,你管理的 Pages 会出现在这里',
    msg_load_failed: '加载失败',
    msg_sync_dispatched: '已派发 {count} 个主页同步任务',
    msg_sync_none: '没有可同步的渠道授权',
    msg_sync_failed: '主页同步失败',
  },
  batchCreate: {
    targeting_pack_section: '定向包(地区组)',
    f_targeting_pack: '使用定向包',
    targeting_pack_ph: '选择已保存的定向包(可选)',
    targeting_pack_help: '选用定向包后,广告组的地区/年龄/性别/兴趣将由定向包统一设置,覆盖手动地区定向。',
    split_section: '拆分数量',
    f_split_count: '每个组合拆分份数',
    split_count_help: '为同一素材/定向做 AB 测试:每个组合复制为 N 个近似广告组(命名后缀 #1/#2/…)。范围 1–10。',
    matrix_title: '连线矩阵(排除组合)',
    matrix_help: '取消勾选不想创建的组合,未勾选的组合将被跳过。',
    matrix_count: '保留 {kept} / 共 {total} 个组合',
    bulk_count: '已选 {n} / 共 {total}',
    bulk_select_all: '全选',
    bulk_clear: '清空',
    bulk_invert: '反选',
    bulk_only_recommended: '仅选推荐',
    bulk_select_filtered: '全选当前筛选结果',
    title: '叉乘批量创建广告',
    subtitle: '商品 × 国家 × 受众 × 素材 × 预算,一次性叉乘生成上百条广告,智能预选高潜商品',
    load_template: '从模板载入',
    recent_jobs: '最近批量任务',
    job_progress: '完成 {done}/{total} · 失败 {failed}',
    // step labels
    step1: '维度选择',
    step2: '组合方式',
    step3: '拆分规则',
    step4: '出价预算与归因',
    step5: '预览确认',
    prev: '上一步',
    next: '下一步',
    // formula bar
    formula_total: '{n} 条广告',
    formula_empty: '请先选择至少一个维度',
    formula_over_limit: '超过单次上限 {max} 条',
    formula_warn: '数量较大,请确认',
    // dimensions
    dim_product: '商品',
    dim_geo: '国家',
    dim_audience: '受众',
    dim_creative: '素材',
    dim_bidbudget: '出价预算',
    only_recommended: '只看推荐商品',
    product_hint: '系统已根据毛利、库存、机会分智能预选高潜商品(标「智能推荐」),带排除原因的商品不可选。',
    badge_recommended: '智能推荐',
    margin_score: '毛利分 {v}',
    inventory_qty: '库存 {v}',
    no_products: '暂无可用商品',
    no_products_desc: '请先在电商店铺同步商品',
    geo_ph: '选择投放国家/地区',
    audience_ph: '选择已保存受众',
    no_creatives: '暂无可用素材',
    budget_hint: '每个预算组都会成为一个独立的出价预算维度元素(日预算,主货币单位)。',
    budget_label: '日预算 ${v}',
    bid_label: '出价 ${v}',
    add_budget: '添加预算组',
    // step1 account
    f_account: '广告账户',
    account_ph: '选择广告账户',
    create_unsupported: '暂不支持创建',
    // step2
    f_mode: '组合方式',
    mode_cross: '全叉乘',
    mode_zip: '按序分配',
    mode_cross_help: '全叉乘:每个维度的元素两两组合,生成所有可能的组合(笛卡尔积)。',
    mode_zip_help: '按序分配:各维度按序号一一对应(第 i 个 × 第 i 个),按最短维度对齐。',
    binds_title: '自由绑定(可选)',
    binds_help: '指定 A 维度的某个元素必须与 B 维度的某个元素同时出现,不满足的组合会被过滤。仅全叉乘模式生效。',
    bind_dim_ph: '维度',
    bind_item_ph: '元素',
    add_bind: '添加绑定',
    bind_incomplete: '请完整选择两个维度的元素',
    bind_same_dim: '绑定的两个元素不能来自同一维度',
    f_binding_mode: '绑定模式',
    binding_cross: '叉乘',
    binding_free: '自由绑定',
    binding_cross_help: '各维度全量叉乘组合(可再按连线/绑定收窄)。',
    binding_free_help: '手动指定每条组合,不做叉乘。',
    free_title: '自由绑定组合',
    free_help: '每行选择一条组合的各维度元素,留空表示该维度不限定。',
    free_no_dims: '请先在上一步选择至少一个维度。',
    free_add: '添加组合',
    free_any: '不限',
    naming_section: '命名通配符',
    naming_help: '可在系列/广告组/广告名称中插入通配符,创建时按组合自动替换。留空则用默认命名。',
    naming_tokens: '可用通配符:',
    naming_insert: '插入通配符',
    naming_ph: '如 {start_time}-{region_code}',
    naming_campaign: '系列名称',
    naming_adset: '广告组名称',
    naming_ad: '广告名称',
    // step3
    split_help: '为每个维度选择拆分层级:决定它在「系列 / 广告组 / 广告」哪一层展开。',
    split_account_rule: '广告账户始终位于系列层级。',
    split_targeting_warn: '定向维度(国家/受众)放在「广告」层级时,Facebook 实际在广告组层级定向,可能不符合预期。',
    tree_preview: '结构预览',
    tree_campaigns: '{n} 个系列',
    tree_adsets: '{n} 个广告组',
    tree_ads: '{n} 条广告',
    level_campaign: '系列',
    level_adset: '广告组',
    level_ad: '广告',
    // step4
    f_objective: '推广目标',
    f_page: '主页',
    f_pixel: '像素(可选)',
    page_ph: '选择主页',
    pixel_ph: '选择像素',
    ladder_toggle: '阶梯出价',
    ladder_min: '最低出价',
    ladder_max: '最高出价',
    inject_utm: '自动注入 UTM 实现单广告利润归因',
    inject_utm_help: '开启后,每条广告自动带上唯一 UTM 参数,实现按广告维度的真实利润归因 —— 这是我们相对 XMP 的核心优势。',
    // step5 preview
    blocked_title: '{n} 个商品被阻止创建',
    poas_baseline_val: '预期 POAS 基线:{poas}(毛利率 {margin}%,来源:{source})',
    col_campaign: '系列',
    col_adset: '广告组',
    col_ad: '广告名',
    col_budget: '预算/出价',
    save_template: '保存为模板',
    start_create: '开始批量创建({n} 条)',
    // submit / confirm
    confirm_submit_title: '确认批量创建',
    confirm_submit: '即将创建 {count} 条广告,确认开始?',
    confirm_submit_ok: '确认创建',
    job_name_title: '任务命名',
    job_name_prompt: '为本次批量任务取个名字',
    job_name_default: '批量创建 {date}',
    job_name_required: '请输入任务名称',
    // execution
    exec_title: '执行中:{name}',
    close_panel: '收起',
    exec_done: '成功 {n}',
    exec_failed: '失败 {n}',
    exec_skipped: '跳过 {n}',
    exec_pending: '待处理 {n}',
    retry_failed: '重试失败项',
    item_status_pending: '待处理',
    item_status_running: '处理中',
    item_status_done: '成功',
    item_status_failed: '失败',
    item_status_skipped: '跳过',
    job_status_pending: '等待中',
    job_status_running: '执行中',
    job_status_done: '已完成',
    job_status_partial: '部分成功',
    job_status_failed: '失败',
    job_status_cancelled: '已取消',
    // templates
    tpl_save_title: '保存模板',
    tpl_save_prompt: '为该批量配置取个模板名',
    tpl_name_required: '请输入模板名称',
    // messages
    msg_load_failed: '加载失败',
    msg_preview_failed: '预览失败',
    msg_submitted: '批量任务已提交',
    msg_submit_failed: '提交失败',
    msg_retry_started: '已重新提交失败项',
    msg_retry_failed: '重试失败',
    msg_tpl_saved: '模板已保存',
    msg_tpl_loaded: '已载入模板「{name}」',
    err_no_account: '请先选择广告账户',
    err_no_dim: '请至少选择一个维度的元素',
  },
  oneClick: {
    title: '一键刊登', subtitle: '粘贴商品 URL + 设预算/目标 → AI 生成广告 → 一键投放',
    step_input: '填写商品与目标', step_preview: '预览广告计划', step_run: '提交并跟踪',
    ad_account: '广告账户', objective: '推广目标', product: '商品', by_url: '粘贴 URL', by_select: '选择已同步商品',
    url_placeholder: '粘贴 Shopify / 店匠 等店铺的商品 URL', select_product: '选择商品',
    url_hint: '一键刊登依赖电商渠道已同步的商品数据;系统不会凭 URL 抓取站外商品。',
    daily_budget: '日预算', target_cpa: '目标 CPA', target_roas: '目标 ROAS', optional: '选填',
    page: '投放主页', pixel: '像素(选填)', keywords: '关键词 / 卖点', keywords_placeholder: '用逗号或换行分隔',
    reference_images: '参考素材', upload_image: '上传图片', reference_hint: '每张参考图会生成一条独立广告;不传则使用商品主图。',
    inject_utm: '注入 UTM 跟踪参数(用于按广告/商品归因)', generate: '生成广告计划',
    pick_account_first: '请先选择广告账户', image_uploaded: '图片已上传',
    view_product: '查看商品', ai_copy: 'AI 广告文案', headline: '标题', primary_text: '正文', description: '描述', cta: '行动号召',
    safety_flags: '文案合规提示:检测到「{words}」,请人工复核。',
    plan: '广告计划', count_campaigns: '{n} 个系列', count_adsets: '{n} 个广告组', count_ads: '{n} 条广告',
    budget_summary: '日预算 {v}', cpa_summary: '目标 CPA {v}', roas_summary: '目标 ROAS {v}', per_day: '天',
    back: '返回修改', submit: '确认并投放', submitted: '已提交,正在创建', launch_another: '再来一单',
    run_done: '成功 {n}', run_failed: '失败 {n}', run_pending: '进行中 {n}', no_accounts: '暂无可投放的广告账户',
    job_status_pending: '排队中', job_status_running: '创建中', job_status_done: '已完成',
    job_status_partial: '部分成功', job_status_failed: '失败', job_status_cancelled: '已取消',
  },
  ruleTemplates: {
    library_btn: '规则模板库',
    drawer_title: '规则模板库',
    drawer_intro: '从下方模板一键创建规则,应用后会以「模拟」状态打开编辑表单,确认无误再启用。',
    save_as: '另存为模板',
    use: '使用此模板',
    delete: '删除',
    builtin_tag: '内置',
    cat_stop_loss: '自动止损',
    cat_repricing: '自动调价',
    cat_budget: '预算管理',
    cat_notify: '通知提醒',
    cat_custom: '自定义',
    empty_title: '暂无模板',
    empty_desc: '内置模板加载失败或已被清空',
    save_dialog_title: '另存为模板',
    form_name: '模板名称',
    form_name_placeholder: '给这个模板起个名字',
    form_desc: '描述',
    form_desc_placeholder: '简要说明这个模板的用途(可选)',
    form_category: '分类',
    cancel: '取消',
    save_btn: '保存模板',
    confirm_delete_title: '删除模板',
    confirm_delete: '确定删除模板「{name}」吗?',
    confirm_delete_label: '删除',
    msg_load_failed: '加载模板失败',
    msg_saved: '模板已保存',
    msg_save_failed: '保存模板失败',
    msg_deleted: '模板已删除',
    msg_delete_failed: '删除模板失败',
    msg_name_required: '请填写模板名称',
  },
  aiCopy: {
    entryButton: 'AI 生成文案',
    title: 'AI 广告文案生成',
    productTitle: '产品名称',
    productTitlePlaceholder: '例如:便携式蓝牙保温杯',
    sellingPoints: '核心卖点',
    sellingPointsPlaceholder: '每行一个卖点,例如:\n12 小时保温\n一键开盖\n食品级不锈钢',
    audience: '目标受众',
    audiencePlaceholder: '例如:25-40 岁通勤白领',
    tone: '语气风格',
    tonePlaceholder: '默认',
    tone_professional: '专业',
    tone_friendly: '亲和',
    tone_urgent: '紧迫',
    tone_playful: '活泼',
    tone_luxury: '高端',
    platform: '投放平台',
    platform_meta: 'Meta(Facebook / Instagram)',
    platform_tiktok: 'TikTok',
    platform_google: 'Google',
    targetLang: '输出语言',
    'lang_zh-CN': '中文',
    'lang_en-US': 'English',
    generate: '生成',
    close: '关闭',
    copy: '复制',
    saveTemplate: '存为模板',
    tabPrimary: '文案',
    tabHeadlines: '标题',
    tabDescriptions: '描述',
    tabCta: 'CTA',
    tabDirections: '测试方向',
    safetyWarning: '检测到可能违反广告政策的措辞,请人工复核:{words}',
    metaLine: '模型 {model} · 预估成本 ${cost}',
    msgNeedTitle: '请先填写产品名称',
    msgGenerated: '文案已生成',
    msgGenerateFailed: '生成失败',
    msgCopied: '已复制到剪贴板',
    msgCopyFailed: '复制失败',
  },
  notifyChannels: {
    title: '通知渠道',
    hint: '把工作区通知推送到飞书 / 钉钉 / 企业微信群机器人或自定义 Webhook,除站内信和邮件外多一条盯盘通道。密钥加密存储,不会回传。',
    refresh: '刷新',
    add: '添加渠道',
    edit: '编辑',
    delete: '删除',
    test: '测试发送',
    save: '保存',
    cancel: '取消',
    error: '异常',
    loadFailed: '加载失败',
    saveFailed: '保存失败',
    deleteFailed: '删除失败',
    created: '已添加',
    updated: '已更新',
    deleted: '已删除',
    testOk: '测试消息已发送',
    testFailed: '测试失败:{error}',
    confirmDelete: '删除通知渠道「{name}」?该渠道将不再接收通知。',
    type: {
      feishu: '飞书',
      dingtalk: '钉钉',
      wecom: '企业微信',
      webhook: '自定义 Webhook',
    },
    level: {
      info: '全部',
      warning: '警告及以上',
      error: '仅错误',
    },
    event: {
      auth_expired: '授权过期',
      sync_failed: '同步失败',
      domain_alert: '域名告警',
      policy_event: '政策事件',
      system: '系统',
      info: '普通信息',
    },
    col: {
      name: '名称',
      type: '类型',
      minLevel: '级别',
      active: '启用',
      actions: '操作',
    },
    field: {
      type: '渠道类型',
      name: '名称',
      namePlaceholder: '例如:运营盯盘群',
      url: 'Webhook 地址',
      urlPlaceholder: 'https://...',
      signEnabled: '启用加签校验',
      secret: '加签密钥',
      secretPlaceholder: '机器人安全设置里的加签密钥',
      secretKeepHint: '(留空 = 不修改现有密钥)',
      eventTypes: '接收事件',
      eventTypesPlaceholder: '不选 = 接收全部事件',
      eventTypesHint: '留空表示接收所有类型的通知。',
      minLevel: '最低级别',
    },
    dialog: {
      addTitle: '添加通知渠道',
      editTitle: '编辑通知渠道',
    },
    rule: {
      name: '请填写名称',
      url: '请填写 Webhook 地址',
    },
    empty: {
      title: '还没有通知渠道',
      desc: '添加飞书 / 钉钉 / 企业微信群机器人或自定义 Webhook,工作区告警会同步推送过去。',
      action: '添加第一个',
    },
  },
  bulkEdit: {
    assign_label: '批量赋值',
    assign_apply: '应用',
    assign_status_ph: '统一开关',
    assign_budget_ph: '统一日预算',
    assign_bid_ph: '统一出价',
    assign_factor_ph: '倍率',
    apply: '应用',
    cancel: '取消',
    error_unknown: '未知错误',
    result_ok: '成功',
    result_fail: '失败',
    col_name: '名称',
    col_status: '状态',
    col_budget: '日预算 (USD)',
    col_bid: '出价 (USD)',
    col_result: '结果',
    status_on: '开',
    status_off: '关',
    tag_unsupported: '不支持',
    table_entry: '表格编辑',
    table_title_campaigns: '批量编辑系列(表格)',
    table_title_adsets: '批量编辑广告组(表格)',
    table_title_ads: '批量编辑广告(表格)',
    table_meta: '共 {count} 行选中',
    table_meta_skipped: '其中 {count} 行所在渠道不支持编辑,已禁用',
    table_no_writable: '没有可编辑的行(选中行所在渠道均不支持)',
    table_no_changes: '没有任何改动',
    table_confirm_title: '确认批量编辑',
    table_confirm_message: '将对 {count} 行应用改动并同步到渠道,确认?',
    table_apply: '应用到 {count} 行',
    table_success: '已成功更新 {ok} 行',
    table_partial: '成功 {ok} 行,失败 {fail} 行',

  },
  byFilterBulk: {
    entry: '对筛选结果批量操作',
    title: '按筛选条件批量操作',
    header: '对符合当前筛选的全部 {total} 条{level}批量操作',
    level_campaign: '广告系列',
    level_adset: '广告组',
    level_ad: '广告',
    action_label: '操作类型',
    action_status: '修改状态',
    action_budget_set: '设置日预算',
    action_budget_factor: '日预算 ×倍率',
    action_bid_factor: '出价 ×倍率',
    status_label: '目标状态',
    status_active: '启用',
    status_paused: '暂停',
    status_archived: '归档',
    budget_label: '日预算',
    budget_ph: '统一日预算',
    currency_suffix: 'USD',
    factor_label: '倍率',
    factor_ph: '如 0.8 / 1.2',
    factor_hint: '在原值基础上 ×倍率',
    writable_note: '仅对可写渠道(facebook)且会产生改动的实体生效;无变化或不可写的实体将自动跳过。',
    invalid_value: '请填写有效的数值(需大于 0)',
    summary_status: '状态 → {status}',
    summary_budget_set: '日预算 → {value}',
    summary_budget_factor: '日预算 ×{factor}',
    summary_bid_factor: '出价 ×{factor}',
    confirm_title: '确认批量操作',
    confirm_message: '将对符合当前筛选的全部 {total} 条{level}执行「{action}」并同步到渠道,确认?',
    confirm_ok: '确认执行',
    submit_btn: '对全部 {total} 条执行',
    submit_failed: '提交任务失败',
    job_name: '按筛选批量操作 {level} · {time}',
    cancel: '取消',
    exec_counts: '已完成 {done}/{total} · 失败 {failed} · 跳过 {skipped}',
    polling: '执行中,自动刷新…',
    field_status: '状态',
    field_daily_budget: '日预算',
    field_bid: '出价',
    col_label: '对象',
    col_op: '操作',
    col_status: '状态',
    col_error: '错误',
    item_status_pending: '待处理',
    item_status_updating: '处理中',
    item_status_done: '成功',
    item_status_failed: '失败',
    item_status_skipped: '已跳过',
    job_status_pending: '等待中',
    job_status_running: '执行中',
    job_status_done: '已完成',
    job_status_failed: '失败',
    job_status_cancelled: '已取消',
    job_status_partial: '部分成功',
    retry_btn: '重试失败项',
    retry_failed: '重试失败',
    close: '关闭',
  },
  creativeLibrary: {
    activeTags: '已选标签',
    select: '多选',
    exitSelect: '退出多选',
    selectedCount: '已选 {count} 个',
    selectFirst: '请先选择素材',
    clearSelection: '清空选择',
    batchTag: '批量打标签',
    batchTagTitle: '批量打标签({count} 个素材)',
    batchTagged: '已为 {count} 个素材添加标签',
    batchUntagged: '已为 {count} 个素材移除标签',
    tagAction: '操作',
    tagAdd: '添加',
    tagRemove: '移除',
    tagDimension: '标签维度',
    tagValue: '标签值',
    tagValuePlaceholder: '例如:痛点、UGC、对比图',
    tagValueRequired: '请输入标签值',
    apply: '应用',
    clearTagFilter: '清除标签筛选',
    tagType_angle: '角度',
    tagType_selling_point: '卖点',
    tagType_format: '形状/格式',
    tagType_hook: '钩子',
    tagType_custom: '自定义',
    realProfit: '真实数据',
    startDate: '开始日期',
    endDate: '结束日期',
    sortNone: '不排序',
    sortPoas: '按真实 POAS',
    sortGrossProfit: '按真实毛利',
    spend: '花费',
    grossProfit: '真实毛利',
    poas: '真实 POAS',
    orders: '订单',
    noProfitData: '暂无利润数据',
    profitLoadFailed: '加载利润数据失败',
    profitCaption: '素材级真实利润(已扣 COGS/退款),XMP 只有 ROAS。',
    mixedCurrency: '数据含多种货币,已统一按 {currency} 展示,金额仅供参考',
    useCreative: '用此素材建广告',
    // Folders
    folderAll: '全部',
    folderUnfiled: '未分类',
    folderNew: '新建文件夹',
    folderNamePlaceholder: '请输入文件夹名称',
    folderNamePrompt: '文件夹名称',
    folderParent: '上级文件夹(可选)',
    folderParentNone: '顶级文件夹',
    folderCreated: '文件夹已创建',
    folderCreateFailed: '创建文件夹失败',
    folderRename: '重命名',
    folderRenamed: '文件夹已重命名',
    folderRenameFailed: '重命名失败',
    folderDelete: '删除文件夹',
    folderDeleteConfirm: '确定删除文件夹「{name}」?其中的素材将变为未分类(不会被删除),子文件夹将上移。',
    folderDeleted: '文件夹已删除',
    folderDeleteFailed: '删除文件夹失败',
    folderNameRequired: '请输入文件夹名称',
    folderLoadFailed: '加载文件夹失败',
    // Batch move
    batchMove: '移动到文件夹',
    moveTitle: '移动 {count} 个素材',
    moveTarget: '目标文件夹',
    moveToUnfiled: '移出文件夹(未分类)',
    moved: '已移动 {count} 个素材',
    moveFailed: '移动失败',
    // Batch upload
    uploadTargetFolder: '目标文件夹',
    uploadFolderNone: '未分类',
    uploadQueue: '上传队列',
    uploadStatus_pending: '等待中',
    uploadStatus_uploading: '上传中',
    uploadStatus_done: '成功',
    uploadStatus_failed: '失败',
    uploadSummary: '成功 {ok} / 失败 {fail}',
    uploadPickFiles: '可多选,上传将按顺序逐个进行',
  },
  creativeEditor: {
    entryButton: '在线编辑',
    editThisImage: '用此图编辑',
    title: '素材在线编辑',
    // Source / loading
    pickImage: '选择图片',
    pickImagePlaceholder: '选择一张本地图片开始编辑',
    sourceLoadFailed: '加载图片失败,请改为从本地选择图片',
    taintedWarning: '该素材来自外部 CDN,无法导出。请改为从本地选择图片。',
    noImage: '请先选择或加载一张图片',
    loadingImage: '正在加载图片…',
    // Aspect ratio
    ratioLabel: '比例',
    ratioOriginal: '原始',
    ratioSquare: '1:1 正方形',
    ratioPortrait: '4:5 竖图',
    ratioStory: '9:16 竖屏/快拍',
    ratioLandscape: '1.91:1 横图',
    // Adjustments
    adjustLabel: '调整',
    brightness: '亮度',
    contrast: '对比度',
    backgroundColor: '背景色',
    backgroundColorHint: '改变比例时,留白区域使用此颜色',
    // Text overlay
    textLabel: '文字',
    addText: '添加文字',
    textContent: '文字内容',
    textContentPlaceholder: '如:限时 5 折',
    fontSize: '字号',
    fontColor: '颜色',
    position: '位置',
    posTop: '上',
    posMiddle: '中',
    posBottom: '下',
    posLeft: '左',
    posCenter: '居中',
    posRight: '右',
    removeText: '删除',
    dragHint: '可在画布上拖动文字调整位置',
    // Save
    saveTargetAccount: '保存到广告账户',
    saveTargetAccountPlaceholder: '选择广告账户',
    saveTargetFolder: '目标文件夹',
    saveFolderNone: '未分类',
    fileName: '文件名',
    fileNamePlaceholder: '输入新素材名称',
    save: '保存为新素材',
    saving: '正在保存…',
    saved: '已保存为新素材',
    saveFailed: '保存失败',
    exportFailed: '导出画布失败',
    pickAccountFirst: '请先选择广告账户',
    nameRequired: '请输入文件名',
    reset: '重置',
    cancel: '取消',
  },
} as const;
